import { create } from 'zustand';
import type { Email, Folder, Label, OutboxItem, ThreadListItem, ThreadStats } from '../types';
import emailApiService from '../services/emailApi.service';
import { emailSyncService } from '../services/emailSync.service';
import type { EmailSyncEvent } from '../services/emailSync.service';
import { desktopNotifications } from '@/lib/desktopNotifications';
import { emailCacheService } from '../services/emailCacheService';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';

// Search options for advanced email search
export interface SearchOptions {
  folder?: string;
  dateFrom?: string;
  dateTo?: string;
  hasAttachment?: boolean;
  from?: string;
  to?: string;
  isUnread?: boolean;
  isStarred?: boolean;
  limit?: number;
  page?: number;
}

interface PrimaryAccount {
  id: number;
  email: string;
  name: string;
  provider: string;
  provider_type: string;
  status?: string;
  domain?: string;
  has_domain_mailbox?: boolean;
  // Connector settings for send-from options
  force_connector_smtp?: boolean;
  default_send_as?: string | null; // Default sending address (primary, alias, or connector email)
  // All domain aliases for "send as" dropdown
  all_aliases?: Array<{
    id: number;
    email: string;
    display_name?: string;
    domain?: string;
    is_identity_primary?: boolean;
  }>;
  linked_connector?: {
    id: number;
    email: string;
    host: string;
    name?: string;
    deliverability_score?: number | null;
    has_delivery_issues?: boolean;
    dkim_verified?: boolean;
    dmarc_verified?: boolean;
    spf_verified?: boolean;
  } | null;
}

// Outbox item for optimistic display
export interface LocalOutboxItem extends OutboxItem {
  tempId: string;  // Local identifier for optimistic updates
  isOptimistic?: boolean; // True until confirmed by server
  sharedMailboxId?: number | null; // For shared mailbox outbox
}

interface EmailStore {
  // State
  folders: Folder[];
  labels: Label[];
  selectedFolder: string;
  loading: boolean;
  error: string | null;
  hasEmailAccount: boolean;
  primaryAccount: PrimaryAccount | null;
  accounts: PrimaryAccount[]; // All user's email accounts for send-from dropdown
  activeSharedMailboxId: number | null; // For viewing shared mailbox emails
  includeSharedMailboxThreads: boolean; // Combined inbox scope when no specific shared mailbox is selected
  sharedMailboxUnreadCounts: Record<number, number>; // Track unread counts per shared mailbox
  syncStatus: {
    connected: boolean;
    syncing: boolean;
    lastSync: Date | null;
  };
  starredCount: number;
  wsConnected: boolean;
  inboxUnreadCount: number; // Tracks INBOX unread count regardless of selected folder
  spamUnreadCount: number; // Tracks Spam unread count for badge display
  currentUserId: number | null; // Current logged-in user ID for read_by_users tracking
  
  // Deep security analysis cache - prevents re-fetching when switching emails
  deepAnalysisCache: Record<string, any>;
  
  // Outbox state - for optimistic updates
  outboxItems: LocalOutboxItem[];
  outboxLoading: boolean;
  
  // Actions
  setFolders: (folders: Folder[]) => void;
  setLabels: (labels: Label[]) => void;
  selectFolder: (folder: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setHasEmailAccount: (has: boolean) => void;
  setPrimaryAccount: (account: PrimaryAccount | null) => void;
  setAccounts: (accounts: PrimaryAccount[]) => void;
  setActiveSharedMailbox: (mailboxId: number | null) => void;
  setIncludeSharedMailboxThreads: (enabled: boolean) => void;
  setSharedMailboxUnreadCount: (mailboxId: number, count: number) => void;
  decrementSharedMailboxUnreadCount: (mailboxId: number) => void;
  setSyncStatus: (status: Partial<{ connected: boolean; syncing: boolean; lastSync: Date | null }>) => void;
  setInboxUnreadCount: (count: number) => void;
  incrementInboxUnreadCount: (by?: number) => void;
  fetchInboxUnreadCount: () => Promise<void>;
  setSpamUnreadCount: (count: number) => void;
  incrementSpamUnreadCount: (by?: number) => void;
  setCurrentUserId: (userId: number) => void;
  
  // Deep analysis cache methods
  cacheDeepAnalysis: (messageId: string, analysis: any) => void;
  getCachedDeepAnalysis: (messageId: string) => any | null;
  clearDeepAnalysisCache: () => void;
  
  // API Actions
  checkEmailAccount: () => Promise<boolean>;
  fetchFolders: () => Promise<void>;
  fetchLabels: () => Promise<void>;
  fetchEmails: (folder?: string, forceRefresh?: boolean, sharedMailboxId?: number | null) => Promise<void>;
  emptyTrash: () => Promise<{ deleted: number } | null>;
  
  // Outbox Actions
  addToOutbox: (item: Omit<LocalOutboxItem, 'tempId'>) => string;
  updateOutboxItem: (tempId: string, updates: Partial<LocalOutboxItem>) => void;
  removeFromOutbox: (tempId: string) => void;
  cancelOutboxItem: (queueId: string) => Promise<boolean>;
  fetchOutbox: (sharedMailboxId?: number | null) => Promise<void>;
  markOutboxItemSent: (tempId: string, messageId?: string) => void;
  markOutboxItemFailed: (tempId: string, error: string) => void;
  
  // WebSocket Sync
  connectWebSocket: () => void;
  disconnectWebSocket: () => void;
  handleSyncEvent: (event: EmailSyncEvent) => void;
  
  // Hydrate memory cache from IndexedDB on startup (for instant folder switching after page refresh)
  hydrateFromIndexedDB: () => Promise<void>;
  
  // Email Threads (MongoDB-based unified messaging)
  threads: ThreadListItem[];
  selectedThread: ThreadListItem | null;
  threadStats: ThreadStats | null;
  threadsLoading: boolean;
  
  // Per-folder thread cache for instant folder switching
  folderThreads: Record<string, ThreadListItem[]>;
  folderThreadsLastSync: Record<string, number>; // timestamp of last server sync per folder
  
  // Pagination state for threads
  threadsHasMore: boolean; // Whether more threads are available to load
  threadsCurrentSkip: number; // Current pagination offset
  threadsTotalCount: number; // Total number of threads available
  threadsSearchQuery: string; // Current server-side search query
  
  // Thread Actions
  fetchThreads: (options?: { folder?: string; unread?: boolean; starred?: boolean; search?: string; accountId?: number; includeSharedMailboxThreads?: boolean; forceRefresh?: boolean; append?: boolean }) => Promise<void>;
  loadMoreThreads: () => Promise<void>;
  setThreadsSearchQuery: (query: string) => void;
  smartSyncThreads: (folder?: string) => Promise<{ synced: boolean; reason: string }>;
  backgroundRefreshThreads: (folder?: string) => Promise<void>;
  selectThread: (thread: ThreadListItem | null) => void;
  markThreadRead: (threadId: string, read?: boolean) => Promise<void>;
  toggleThreadStar: (threadId: string, starred: boolean) => Promise<void>;
  deleteThread: (threadId: string) => Promise<void>;
  moveThread: (threadId: string, toFolder: string) => Promise<boolean>;
  deleteThreads: (threadIds: string[]) => Promise<void>;
  moveThreads: (threadIds: string[], toFolder: string) => Promise<void>;
  fetchThreadStats: () => Promise<void>;
  
  // Reset
  reset: () => void;
}

const initialState = {
  folders: [],
  labels: [
    { name: 'Important', count: 0, color: '#ef4444' },
    { name: 'Work', count: 0, color: '#10b981' },
    { name: 'Personal', count: 0, color: '#8b5cf6' },
  ],
  selectedFolder: 'INBOX',
  loading: false,
  error: null,
  hasEmailAccount: false,
  primaryAccount: null,
  accounts: [],
  activeSharedMailboxId: null,
  includeSharedMailboxThreads: false,
  sharedMailboxUnreadCounts: {},
  syncStatus: {
    connected: false,
    syncing: false,
    lastSync: null,
  },
  starredCount: 0,
  wsConnected: false,
  inboxUnreadCount: 0,
  spamUnreadCount: 0,
  currentUserId: null,
  // Outbox state
  outboxItems: [] as LocalOutboxItem[],
  outboxLoading: false,
  // Threads state (MongoDB-based unified messaging)
  threads: [] as ThreadListItem[],
  selectedThread: null as ThreadListItem | null,
  threadStats: null as ThreadStats | null,
  threadsLoading: false,
  // Per-folder thread cache for instant folder switching (memory cache)
  folderThreads: {} as Record<string, ThreadListItem[]>,
  folderThreadsLastSync: {} as Record<string, number>, // timestamp of last server sync per folder
  // Pagination state
  threadsHasMore: false,
  threadsCurrentSkip: 0,
  threadsTotalCount: 0,
  threadsSearchQuery: '',
  // Deep security analysis cache - keyed by messageId
  deepAnalysisCache: {} as Record<string, any>,
};

let emailSyncStoreSubscribers = 0;
let emailSyncEventUnsubscribe: (() => void) | null = null;
let emailSyncConnectionUnsubscribe: (() => void) | null = null;

type ThreadFolderStatsEntry = number | { total?: number; unread?: number };
type EventParticipant = string | { name?: string; email?: string; address?: string } | undefined;

function getFolderStatsValue(entry: ThreadFolderStatsEntry | undefined, key: 'total' | 'unread'): number {
  if (typeof entry === 'number') {
    return entry;
  }

  return entry?.[key] ?? 0;
}

function getThreadScopeCacheAccountId(accountId: number | null | undefined, includeSharedMailboxThreads = false): number {
  if (includeSharedMailboxThreads && !accountId) {
    return -1;
  }

  return accountId || 0;
}

function getThreadScopeCacheKey(folder: string, accountId: number | null | undefined, includeSharedMailboxThreads = false): string {
  return `${folder}:${getThreadScopeCacheAccountId(accountId, includeSharedMailboxThreads)}`;
}

function mapThreadSummary(t: any): ThreadListItem {
  return {
    thread_id: t.thread_id || t._id,
    subject: t.subject,
    participants: t.participants || (t.from ? [t.from] : []),
    last_message_at: t.last_message_at || t.date,
    last_message_preview: t.last_message_preview || (t.text_content ? t.text_content.substring(0, 200) : ''),
    last_message_from: t.last_message_from || t.from,
    folder: t.folder,
    labels: t.labels,
    has_unread: t.has_unread ?? !t.is_read,
    unread_count: t.unread_count ?? (t.is_read ? 0 : 1),
    starred: t.starred || t.flags?.flagged || false,
    message_count: t.message_count || 1,
    messages: t.messages,
    read_by_users: t.read_by_users || [],
    mailbox: t.mailbox,
    mailbox_label: t.mailbox_label,
    account_id: typeof t.account_id === 'number' ? t.account_id : Number(t.account_id) || undefined,
    last_message_folder: t.last_message_folder,
    last_sent_at: t.last_sent_at,
  };
}

export const useEmailStore = create<EmailStore>((set, get) => ({
  ...initialState,
  
  // Basic setters
  setFolders: (folders: Folder[]) => set({ folders }),
  setLabels: (labels: Label[]) => set({ labels }),
  
  selectFolder: (folder: string) => {
    // Use memory-cached threads for INSTANT display
    // CRITICAL: Use combined folder:accountId key to prevent mixing personal and shared mailbox threads
    const folderCacheKey = getThreadScopeCacheKey(
      folder,
      get().activeSharedMailboxId,
      get().includeSharedMailboxThreads && !get().activeSharedMailboxId,
    );
    const cachedThreads = get().folderThreads[folderCacheKey] || [];
    const hasCache = cachedThreads.length > 0;
    
    console.log(`📧 [selectFolder] Switching to "${folder}"`, {
      folderCacheKey,
      cachedThreadsCount: cachedThreads.length,
      hasCache,
      folderThreadsSnapshot: Object.fromEntries(
        Object.entries(get().folderThreads).map(([k, v]) => [k, (v as any[]).length])
      )
    });
    
    // IMPORTANT: Only update threads if we have cached data
    // If no cache, keep current threads visible (prevents flash) - fetchThreads will load data
    if (hasCache) {
      set({ 
        selectedFolder: folder, 
        selectedThread: null,
        threads: cachedThreads, // Show cached threads instantly
        threadsLoading: false
      });
    } else {
      // No memory cache - just update folder selection and show loading
      // DON'T set threads: [] as that causes the flash
      set({ 
        selectedFolder: folder, 
        selectedThread: null,
        threadsLoading: true // Show loading indicator
      });
    }
    
    // Note: fetchThreads will be called separately by the page component
    // It will load from IndexedDB or API and update threads
  },
  
  setLoading: (loading: boolean) => set({ loading }),
  setError: (error: string | null) => set({ error }),
  setHasEmailAccount: (has: boolean) => set({ hasEmailAccount: has }),
  setPrimaryAccount: (account: PrimaryAccount | null) => set({ primaryAccount: account }),
  setAccounts: (accounts: PrimaryAccount[]) => set({ accounts }),
  setActiveSharedMailbox: (mailboxId: number | null) => set({ activeSharedMailboxId: mailboxId }),
  setIncludeSharedMailboxThreads: (enabled: boolean) => set({ includeSharedMailboxThreads: enabled }),
  
  setSharedMailboxUnreadCount: (mailboxId: number, count: number) => set((state) => ({
    sharedMailboxUnreadCounts: { ...state.sharedMailboxUnreadCounts, [mailboxId]: count }
  })),
  
  decrementSharedMailboxUnreadCount: (mailboxId: number) => set((state) => ({
    sharedMailboxUnreadCounts: {
      ...state.sharedMailboxUnreadCounts,
      [mailboxId]: Math.max(0, (state.sharedMailboxUnreadCounts[mailboxId] || 0) - 1)
    }
  })),
  
  setSyncStatus: (status: Partial<{ connected: boolean; syncing: boolean; lastSync: Date | null }>) => set((state) => ({
    syncStatus: { ...state.syncStatus, ...status }
  })),
  
  setInboxUnreadCount: (count: number) => set({ inboxUnreadCount: count }),
  
  setCurrentUserId: (userId: number) => set({ currentUserId: userId }),
  
  incrementInboxUnreadCount: (by: number = 1) => set((state) => ({
    inboxUnreadCount: state.inboxUnreadCount + by
  })),
  
  setSpamUnreadCount: (count: number) => set({ spamUnreadCount: count }),
  
  incrementSpamUnreadCount: (by: number = 1) => set((state) => ({
    spamUnreadCount: state.spamUnreadCount + by
  })),
  
  // Deep analysis cache methods - cache results to avoid re-fetching
  cacheDeepAnalysis: (messageId: string, analysis: any) => set((state) => ({
    deepAnalysisCache: { ...state.deepAnalysisCache, [messageId]: analysis }
  })),
  
  getCachedDeepAnalysis: (messageId: string) => {
    return get().deepAnalysisCache[messageId] || null;
  },
  
  clearDeepAnalysisCache: () => set({ deepAnalysisCache: {} }),
  
  fetchInboxUnreadCount: async () => {
    try {
      // Fetch just inbox to get unread count
      const response = await emailApiService.getEmails('INBOX', { sharedMailboxId: get().activeSharedMailboxId });
      if (response.success && response.data) {
        const unreadCount = response.data.filter((e: any) => 
          !(e.read ?? e.is_read ?? e.flags?.seen ?? false)
        ).length;
        set({ inboxUnreadCount: unreadCount });
      }
    } catch (error) {
      console.error('Error fetching inbox unread count:', error);
    }
  },
  
  // API Actions
  checkEmailAccount: async () => {
    try {
      const response = await emailApiService.getAccounts();
      const hasAccount = Boolean(response.success && Array.isArray(response.data) && response.data.length > 0);
      set({ hasEmailAccount: hasAccount });
      
      // Store all accounts for send-from dropdown
      if (hasAccount && response.data) {
        const allAccounts: PrimaryAccount[] = response.data.map((acc: any) => ({
          id: acc.id,
          email: acc.email,
          name: acc.name,
          provider: acc.provider,
          provider_type: acc.provider_type,
          status: acc.status,
          domain: acc.domain,
          has_domain_mailbox: acc.has_domain_mailbox || false,
          force_connector_smtp: acc.force_connector_smtp || false,
          default_send_as: acc.default_send_as || null,
          all_aliases: acc.all_aliases || [],
          linked_connector: acc.linked_connector || null
        }));
        
        // Sort: non-totalhub.online emails first, then totalhub.online
        allAccounts.sort((a, b) => {
          const aIsTotalhub = a.email.endsWith('.totalhub.online');
          const bIsTotalhub = b.email.endsWith('.totalhub.online');
          if (!aIsTotalhub && bIsTotalhub) return -1;
          if (aIsTotalhub && !bIsTotalhub) return 1;
          return 0;
        });
        
        set({ accounts: allAccounts });
        
        // Set primary account (first in sorted list)
        const primary = allAccounts[0];
        set({ 
          primaryAccount: {
            id: primary.id,
            email: primary.email,
            name: primary.name,
            provider: primary.provider,
            provider_type: primary.provider_type,
            status: primary.status,
            domain: primary.domain,
            has_domain_mailbox: (response as any).has_domain_mailbox,
            // Include connector settings for send-from options
            force_connector_smtp: primary.force_connector_smtp || false,
            default_send_as: primary.default_send_as || null,
            // All domain aliases for "send as" dropdown
            all_aliases: primary.all_aliases || [],
            linked_connector: primary.linked_connector || null
          }
        });
      } else {
        set({ primaryAccount: null, accounts: [] });
      }
      
      return hasAccount;
    } catch (error) {
      console.error('Error checking email account:', error);
      set({ hasEmailAccount: false, primaryAccount: null, accounts: [] });
      return false;
    }
  },
  
  /** @deprecated Use fetchThreads directly. This is a backward-compat wrapper. */
  fetchEmails: async (folder?: string, _autoSyncIfEmpty = false, sharedMailboxId?: number | null) => {
    const mailboxIdToUse = sharedMailboxId !== undefined ? sharedMailboxId : get().activeSharedMailboxId;
    const includeSharedMailboxThreads = get().includeSharedMailboxThreads && !mailboxIdToUse;
    return get().fetchThreads({ 
      folder: folder || get().selectedFolder,
      accountId: mailboxIdToUse || undefined,
      includeSharedMailboxThreads,
    });
  },
  
  fetchFolders: async () => {
    try {
      const response = await emailApiService.getFolders();
      
      if (response.success && response.data) {
        const folders = response.data;
        const inboxFolder = folders.find((folder) => {
          const folderName = String(folder.path || folder.name || '').toUpperCase();
          return folder.type === 'inbox' || folderName === 'INBOX';
        });
        const spamUnreadCount = folders.reduce((total, folder) => {
          const folderName = String(folder.path || folder.name || '').toUpperCase();
          const isSpamFolder = folder.type === 'spam' || folderName === 'SPAM' || folderName === 'JUNK';
          return total + (isSpamFolder ? (folder.unreadCount || 0) : 0);
        }, 0);

        set({
          folders,
          inboxUnreadCount: inboxFolder?.unreadCount || 0,
          spamUnreadCount,
        });
      } else {
        // Fallback to standard folders if API fails
        const standardFolders: Folder[] = [
          { id: 'inbox', name: 'INBOX', path: 'INBOX', unreadCount: 0, totalCount: 0, type: 'inbox' },
          { id: 'sent', name: 'Sent', path: 'Sent', unreadCount: 0, totalCount: 0, type: 'sent' },
          { id: 'drafts', name: 'Drafts', path: 'Drafts', unreadCount: 0, totalCount: 0, type: 'drafts' },
          { id: 'junk', name: 'Junk', path: 'Junk', unreadCount: 0, totalCount: 0, type: 'spam' },
          { id: 'trash', name: 'Trash', path: 'Trash', unreadCount: 0, totalCount: 0, type: 'trash' },
          { id: 'archive', name: 'Archive', path: 'Archive', unreadCount: 0, totalCount: 0, type: 'custom' },
        ];
        set({ folders: standardFolders, inboxUnreadCount: 0, spamUnreadCount: 0 });
      }
    } catch (error) {
      console.error('[emailStore] Error fetching folders:', error);
      // Fallback to standard folders on error
      const standardFolders: Folder[] = [
        { id: 'inbox', name: 'INBOX', path: 'INBOX', unreadCount: 0, totalCount: 0, type: 'inbox' },
        { id: 'sent', name: 'Sent', path: 'Sent', unreadCount: 0, totalCount: 0, type: 'sent' },
        { id: 'drafts', name: 'Drafts', path: 'Drafts', unreadCount: 0, totalCount: 0, type: 'drafts' },
        { id: 'junk', name: 'Junk', path: 'Junk', unreadCount: 0, totalCount: 0, type: 'spam' },
        { id: 'trash', name: 'Trash', path: 'Trash', unreadCount: 0, totalCount: 0, type: 'trash' },
        { id: 'archive', name: 'Archive', path: 'Archive', unreadCount: 0, totalCount: 0, type: 'custom' },
      ];
      set({ folders: standardFolders, inboxUnreadCount: 0, spamUnreadCount: 0 });
    }
  },
  
  /**
   * Hydrate memory cache from IndexedDB on startup
   * This loads cached threads into memory BEFORE API calls, enabling instant folder switching
   * even after a page refresh (Ctrl+F5)
   */
  hydrateFromIndexedDB: async () => {
    const standardFolderPaths = ['INBOX', 'Sent', 'Drafts', 'Junk', 'Trash', 'Archive', 'Starred', 'Spam'];
    const accountId = get().primaryAccount?.id || 0;
    
    console.log('📧 [Hydrate] Loading IndexedDB cache into memory...');
    
    try {
      // Initialize the cache service first
      await emailCacheService.init();
      
      // Load all folders from IndexedDB in parallel
      const hydratePromises = standardFolderPaths.map(async (folder) => {
        try {
          const cachedThreads = await emailCacheService.getThreads(folder, accountId);
          if (cachedThreads.length > 0) {
            console.log(`📧 [Hydrate] Loaded ${cachedThreads.length} threads for ${folder} from IndexedDB`);
            return { folder, threads: cachedThreads };
          }
          return null;
        } catch (error) {
          console.warn(`[Hydrate] Failed to load ${folder} from IndexedDB:`, error);
          return null;
        }
      });
      
      const results = await Promise.all(hydratePromises);
      
      // Update memory cache with all loaded threads
      // CRITICAL: Use composite key (folder:accountId) to match selectFolder/fetchThreads/deleteThread
      const newFolderThreads: Record<string, ThreadListItem[]> = {};
      let totalLoaded = 0;
      
      results.forEach((result) => {
        if (result) {
          const cacheKey = `${result.folder}:${accountId}`;
          newFolderThreads[cacheKey] = result.threads;
          totalLoaded += result.threads.length;
        }
      });
      
      if (Object.keys(newFolderThreads).length > 0) {
        set((state) => {
          // IMPORTANT: Don't overwrite existing in-memory folder threads
          // They may have been updated by deleteThread/archiveThread optimistically
          // and contain more recent state than IndexedDB (which updates async)
          const mergedFolderThreads = { ...state.folderThreads };
          for (const [key, threads] of Object.entries(newFolderThreads)) {
            // Only hydrate if there's no existing in-memory cache for this folder
            if (!mergedFolderThreads[key] || mergedFolderThreads[key].length === 0) {
              mergedFolderThreads[key] = threads;
            }
          }
          
          // Set current folder's threads from the composite key
          const selectedFolderKey = `${state.selectedFolder}:${accountId}`;
          const hydratedCurrentFolder = mergedFolderThreads[selectedFolderKey];
          
          return {
            folderThreads: mergedFolderThreads,
            // Only update threads if we have hydrated data for the current folder
            // AND there are no existing threads displayed (prevents overwriting delete state)
            threads: (state.threads.length === 0 && hydratedCurrentFolder) 
              ? hydratedCurrentFolder 
              : state.threads
          };
        });
        
        console.log(`📧 [Hydrate] Complete - loaded ${totalLoaded} threads across ${Object.keys(newFolderThreads).length} folders`);
      } else {
        console.log('📧 [Hydrate] No cached threads found in IndexedDB');
      }
    } catch (error) {
      console.warn('[Hydrate] Failed to hydrate from IndexedDB:', error);
    }
  },
  
  fetchLabels: async () => {
    try {
      const response = await emailApiService.getLabels();
      
      if (response.success && response.data) {
        set({ labels: response.data });
      }
    } catch (error) {
      console.error('Error fetching labels:', error);
    }
  },
  
  emptyTrash: async () => {
    try {
      const response = await emailApiService.emptyTrash();
      if (response.success && response.data) {
        const accountId = get().activeSharedMailboxId || 0;
        const trashCacheKey = `Trash:${accountId}`;
        // Clear all emails if we're viewing trash
        const currentFolder = get().selectedFolder;
        if (currentFolder.toLowerCase().includes('trash')) {
          set({ threads: [], selectedThread: null });
        }
        // Clear Trash folder from memory cache and invalidate timestamp
        set((state) => {
          const updatedFolderThreads = { ...state.folderThreads };
          delete updatedFolderThreads[trashCacheKey];
          const updatedLastSync = { ...state.folderThreadsLastSync };
          delete updatedLastSync[trashCacheKey];
          return { folderThreads: updatedFolderThreads, folderThreadsLastSync: updatedLastSync };
        });
        return { deleted: response.data.deleted };
      }
      return null;
    } catch (error) {
      console.error('Error emptying trash:', error);
      return null;
    }
  },
  
  // ============================================================================
  // OUTBOX ACTIONS - For optimistic updates when sending emails
  // ============================================================================
  
  /**
   * Add an email to the outbox immediately after queueing for send.
   * This provides instant UI feedback without waiting for server confirmation.
   */
  addToOutbox: (item: Omit<LocalOutboxItem, 'tempId'>) => {
    const tempId = `outbox-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const outboxItem: LocalOutboxItem = {
      ...item,
      tempId,
      isOptimistic: true,
    };
    
    set((state) => ({
      outboxItems: [outboxItem, ...state.outboxItems]
    }));
    
    console.log('[EmailStore] Added to outbox:', tempId, item.subject);
    return tempId;
  },
  
  /**
   * Update an outbox item (e.g., status change, progress update)
   */
  updateOutboxItem: (tempId: string, updates: Partial<LocalOutboxItem>) => {
    set((state) => ({
      outboxItems: state.outboxItems.map((item) =>
        item.tempId === tempId ? { ...item, ...updates } : item
      )
    }));
  },
  
  /**
   * Remove an item from the outbox (e.g., after successful send or cancellation)
   */
  removeFromOutbox: (tempId: string) => {
    set((state) => ({
      outboxItems: state.outboxItems.filter((item) => item.tempId !== tempId)
    }));
    console.log('[EmailStore] Removed from outbox:', tempId);
  },
  
  /**
   * Fetch outbox items from the server and merge with optimistic items.
   * Optionally filter by shared mailbox ID.
   * @param sharedMailboxId - number for shared mailbox, null for personal only, undefined for all
   */
  fetchOutbox: async (sharedMailboxId?: number | null) => {
    set({ outboxLoading: true });
    
    try {
      // Pass sharedMailboxId to API - server-side filtering
      const response = await emailApiService.getOutbox({ 
        status: 'all', 
        limit: 50,
        sharedMailboxId: sharedMailboxId
      });
      
      if (response.success && response.data?.items) {
        const serverItems: LocalOutboxItem[] = response.data.items.map((item) => ({
          ...item,
          tempId: item.queueId || item.id,
          isOptimistic: false,
          sharedMailboxId: (item as any).sharedMailboxId || null,
        }));
        
        // Merge: keep optimistic items that don't have server confirmation yet.
        // Discard stale optimistic items to prevent ghost badges:
        //   - Items with a real queueId (not temp-*) that are absent from server list
        //     have already been processed/removed — safe to drop.
        //   - Items still holding a temp queueId that are older than 60 s are also
        //     considered stale (send succeeded or failed without a WS event).
        const now = Date.now();
        const STALE_MS = 60_000;
        let currentOptimistic = get().outboxItems.filter((item) => {
          if (!item.isOptimistic) return false;
          // If already reflected in server list, drop the optimistic copy
          if (serverItems.some(s => s.queueId === item.queueId || s.queueId === item.tempId)) return false;
          // If it has a real (non-temp) queueId but isn't in server list, it's gone — drop it
          const isRealQueueId = item.queueId && !String(item.queueId).startsWith('temp-');
          if (isRealQueueId) return false;
          // Keep truly in-flight temp items that are fresh (< 60 s old)
          const queuedAt = item.queuedAt ? new Date(item.queuedAt).getTime() : 0;
          if (queuedAt && now - queuedAt > STALE_MS) return false;
          return true;
        });
        
        // Filter optimistic items by shared mailbox too
        if (sharedMailboxId !== undefined) {
          currentOptimistic = currentOptimistic.filter(
            (item) => item.sharedMailboxId === sharedMailboxId
          );
        }
        
        set({
          outboxItems: [...currentOptimistic, ...serverItems],
          outboxLoading: false
        });
      } else {
        set({ outboxLoading: false });
      }
    } catch (error) {
      console.error('[EmailStore] Error fetching outbox:', error);
      set({ outboxLoading: false });
    }
  },
  
  /**
   * Mark an outbox item as successfully sent.
   * Removes it from the outbox after a brief delay for visual feedback.
   */
  markOutboxItemSent: (tempId: string, messageId?: string) => {
    set((state) => ({
      outboxItems: state.outboxItems.map((item) =>
        item.tempId === tempId 
          ? { ...item, status: 'sent' as const, isOptimistic: false }
          : item
      )
    }));
    
    // Remove from outbox after 2 seconds (allow user to see success state)
    setTimeout(() => {
      get().removeFromOutbox(tempId);
    }, 2000);
  },
  
  /**
   * Mark an outbox item as failed.
   * Keeps it in the outbox so user can retry.
   */
  markOutboxItemFailed: (tempId: string, error: string) => {
    set((state) => ({
      outboxItems: state.outboxItems.map((item) =>
        item.tempId === tempId 
          ? { ...item, status: 'failed' as const, error, isOptimistic: false, canRetry: true }
          : item
      )
    }));
  },

  /**
   * Cancel/delete an outbox item via API and remove from local state.
   * Works for queued, failed, or scheduled emails.
   */
  cancelOutboxItem: async (queueId: string) => {
    // Get token from authService or fallback to localStorage
    let accessToken = authService.getTokens()?.access_token;
    
    if (!accessToken && typeof window !== 'undefined') {
      try {
        const storedTokens = localStorage.getItem('auth_tokens');
        if (storedTokens) {
          const parsed = JSON.parse(storedTokens);
          accessToken = parsed.access_token;
        }
      } catch (e) {
        console.error('[EmailStore] Failed to parse stored tokens:', e);
      }
    }
    
    if (!accessToken) {
      console.error('[EmailStore] No auth token for cancel outbox');
      return false;
    }

    try {
      const response = await fetch(`/api/v1/email/outbox/${queueId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const result = await response.json();
      
      if (result.success) {
        // Remove from local state - find by queueId or tempId
        set((state) => ({
          outboxItems: state.outboxItems.filter(
            (item) => item.queueId !== queueId && item.tempId !== queueId
          )
        }));
        console.log('[EmailStore] Cancelled outbox item:', queueId);
        return true;
      } else {
        console.error('[EmailStore] Failed to cancel outbox item:', result.error);
        return false;
      }
    } catch (error) {
      console.error('[EmailStore] Error cancelling outbox item:', error);
      return false;
    }
  },
  
  // WebSocket Sync Methods
  connectWebSocket: () => {
    emailSyncStoreSubscribers += 1;

    if (!emailSyncEventUnsubscribe) {
      emailSyncEventUnsubscribe = emailSyncService.subscribe((event) => {
        get().handleSyncEvent(event);
      });
    }

    if (!emailSyncConnectionUnsubscribe) {
      emailSyncConnectionUnsubscribe = emailSyncService.onConnectionChange((connected) => {
        set({ wsConnected: connected });
        set((state) => ({
          syncStatus: { ...state.syncStatus, connected }
        }));
      });
    }

    emailSyncService.connect();
  },
  
  disconnectWebSocket: () => {
    emailSyncStoreSubscribers = Math.max(0, emailSyncStoreSubscribers - 1);

    if (emailSyncStoreSubscribers === 0) {
      emailSyncEventUnsubscribe?.();
      emailSyncEventUnsubscribe = null;

      emailSyncConnectionUnsubscribe?.();
      emailSyncConnectionUnsubscribe = null;

      emailSyncService.disconnect();
      set((state) => ({
        wsConnected: false,
        syncStatus: { ...state.syncStatus, connected: false }
      }));
    }
  },
  
  handleSyncEvent: (event: EmailSyncEvent) => {
    console.log('[EmailStore] Handling sync event:', event.type, event.action);

    const normalizeEventMailboxId = (value: unknown): number | null => {
      const parsed = Number.parseInt(String(value ?? ''), 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    };

    const getPersonalMailboxAggregateKey = () => 0;

    const getPersonalMailboxIds = () => new Set(
      get().accounts
        .map((account) => normalizeEventMailboxId(account.id))
        .filter((id): id is number => id !== null)
    );

    const isSharedMailboxEvent = (mailboxId: number | null) => {
      if (!mailboxId) return false;

      const personalMailboxIds = getPersonalMailboxIds();
      if (personalMailboxIds.has(mailboxId)) {
        return false;
      }

      const sharedCounts = get().sharedMailboxUnreadCounts;
      return Object.prototype.hasOwnProperty.call(sharedCounts, mailboxId) || get().activeSharedMailboxId === mailboxId;
    };

    const getCacheAccountKeyForMailboxEvent = (mailboxId: number | null) => {
      return isSharedMailboxEvent(mailboxId) ? mailboxId! : getPersonalMailboxAggregateKey();
    };

    const eventBelongsToCurrentView = (mailboxId: number | null) => {
      const activeSharedMailboxId = get().activeSharedMailboxId;

      if (isSharedMailboxEvent(mailboxId)) {
        return activeSharedMailboxId === mailboxId;
      }

      return !activeSharedMailboxId;
    };

    const incrementUnreadCountForMailboxEvent = (mailboxId: number | null, by: number = 1) => {
      if (by <= 0) return;

      if (isSharedMailboxEvent(mailboxId) && mailboxId) {
        set((state) => ({
          sharedMailboxUnreadCounts: {
            ...state.sharedMailboxUnreadCounts,
            [mailboxId]: (state.sharedMailboxUnreadCounts[mailboxId] || 0) + by,
          },
        }));
        return;
      }

      get().incrementInboxUnreadCount(by);
    };

    const getEventFromAddress = (from: EventParticipant) => {
      if (typeof from === 'string') {
        return from;
      }

      return from?.address || from?.email || '';
    };

    const getEventFromName = (from: EventParticipant) => {
      if (typeof from === 'string') {
        return from.split('@')[0] || from;
      }

      return from?.name || from?.address || from?.email || 'Unknown';
    };
    
    // Handle different event types
    switch (event.type) {
      case 'email_sync':
        switch (event.action) {
          case 'mark_read':
            // Thread read status is handled by 'thread_read_status_changed' event
            break;
            
          case 'toggle_star':
            // Thread star status is handled by thread-level operations
            break;
            
          case 'move':
            // Thread moves are handled by 'email_moved' and 'thread_moved' events
            break;
            
          case 'delete':
            // Thread deletes are handled by thread-level operations
            break;
            
          case 'new_email':
          case 'new_emails':
            // Handle both singular and plural actions (backend uses 'new_emails')
            const selectedFolder = get().selectedFolder;
            let newInboxEmailsCount = 0;
            let hasNewUnreadEmail = false;
            let lastEmailData: { fromName: string; fromEmail: string; subject: string; messageId: string } | null = null;
            
            // For array of emails (new_emails action)
            if (event.emails && Array.isArray(event.emails)) {
              const emailsToAdd = event.emails;
              const newThreadsToAdd: ThreadListItem[] = [];
              const threadsToUpdate: Array<{ threadId: string; last_message_at: string; last_message_preview: string; last_message_from: any; has_unread: boolean; unread_count: number; message_count: number }> = [];
              
              emailsToAdd.forEach((emailData) => {
                const folder = emailData.folder || 'INBOX';
                const isUnread = !(emailData.is_read ?? emailData.read ?? false);
                const mailboxId = normalizeEventMailboxId(emailData.accountId);
                
                // Track unread inbox emails for badge
                if (folder === 'INBOX' && isUnread) {
                  newInboxEmailsCount++;
                  hasNewUnreadEmail = true;
                  incrementUnreadCountForMailboxEvent(mailboxId, 1);
                }
                
                // Add to visible list if viewing this folder
                if (folder === selectedFolder && eventBelongsToCurrentView(mailboxId)) {
                  // Add to threads state
                  // Check by threadId OR messageId since they could match
                  const threadId = emailData.threadId || emailData.thread_id || emailData.messageId;
                  const existingThread = get().threads.find((t: ThreadListItem) => 
                    t.thread_id === threadId || t.thread_id === emailData.messageId
                  );
                  
                  // Normalize from object - WebSocket sends {address, name}, but ThreadListItem expects {email, name, address}
                  let fromObj: { email: string; name: string; address?: string };
                  if (typeof emailData.from === 'string') {
                    // Plain email string - derive name from prefix
                    fromObj = { email: emailData.from, name: emailData.from.split('@')[0], address: emailData.from };
                  } else if (emailData.from) {
                    // Object with address/name - normalize to include both 'email' and 'address' fields
                    const fromEmail = emailData.from.address || emailData.from.email || 'unknown';
                    const fromName = emailData.from.name || fromEmail.split('@')[0];
                    fromObj = { email: fromEmail, name: fromName, address: fromEmail };
                  } else {
                    fromObj = { email: 'unknown', name: 'Unknown', address: 'unknown' };
                  }
                  
                  if (!existingThread) {
                    const newThread: ThreadListItem = {
                      thread_id: threadId,
                      subject: emailData.subject || '(No subject)',
                      participants: [fromObj],
                      last_message_at: emailData.date || new Date().toISOString(),
                      last_message_preview: emailData.snippet || '',
                      last_message_from: fromObj,
                      folder: folder,
                      labels: [],
                      has_unread: isUnread,
                      unread_count: isUnread ? 1 : 0,
                      starred: false,
                      message_count: 1,
                    };
                    newThreadsToAdd.push(newThread);
                    console.log(`[EmailStore] Creating instant thread for new email: ${threadId}`);
                  } else {
                    // Update existing thread with new message data (reply in existing thread)
                    threadsToUpdate.push({
                      threadId: existingThread.thread_id,
                      last_message_at: emailData.date || new Date().toISOString(),
                      last_message_preview: emailData.snippet || '',
                      last_message_from: fromObj,
                      has_unread: isUnread || existingThread.has_unread,
                      unread_count: (existingThread.unread_count || 0) + (isUnread ? 1 : 0),
                      message_count: (existingThread.message_count || 1) + 1,
                    });
                    console.log(`[EmailStore] Updating existing thread with new message: ${existingThread.thread_id}`);
                  }
                }
              });
              
              // Add new threads and update existing threads immediately (BEFORE the API fetch)
              if (newThreadsToAdd.length > 0 || threadsToUpdate.length > 0) {
                const accountId = get().activeSharedMailboxId || 0;
                const cacheKey = `${selectedFolder}:${accountId}`;
                set((state) => {
                  let existingThreads = [...state.threads];
                  let existingCached = [...(state.folderThreads[cacheKey] || [])];
                  
                  // Update existing threads with new message data (replies)
                  if (threadsToUpdate.length > 0) {
                    const updateMap = new Map(threadsToUpdate.map(u => [u.threadId, u]));
                    existingThreads = existingThreads.map(t => {
                      const update = updateMap.get(t.thread_id);
                      if (update) {
                        return { ...t, ...update, threadId: undefined } as ThreadListItem;
                      }
                      return t;
                    });
                    existingCached = existingCached.map(t => {
                      const update = updateMap.get(t.thread_id);
                      if (update) {
                        return { ...t, ...update, threadId: undefined } as ThreadListItem;
                      }
                      return t;
                    });
                    console.log(`[EmailStore] Updated ${threadsToUpdate.length} existing threads with new messages`);
                  }
                  
                  // Prepend new threads (most recent first)
                  const updatedThreads = [...newThreadsToAdd, ...existingThreads];
                  const updatedCached = [...newThreadsToAdd, ...existingCached];
                  
                  console.log(`[EmailStore] Added ${newThreadsToAdd.length} new threads, updated ${threadsToUpdate.length} existing. Total now: ${updatedThreads.length}`);

                  // While a server-side search is active the visible list holds
                  // filtered results — only update the folder cache so the new
                  // threads surface when the search is cleared.
                  return {
                    ...(state.threadsSearchQuery ? {} : { threads: updatedThreads }),
                    folderThreads: { ...state.folderThreads, [cacheKey]: updatedCached },
                    // Don't invalidate sync time - we just added fresh data
                  };
                });
              }
              
              // Capture one notification payload for the batch
              if (newInboxEmailsCount > 0) {
                // Store last email for notification (we'll show one notification for the batch)
                const lastEmail = emailsToAdd[emailsToAdd.length - 1];
                if (lastEmail) {
                  lastEmailData = {
                    fromName: getEventFromName(lastEmail.from),
                    fromEmail: getEventFromAddress(lastEmail.from),
                    subject: lastEmail.subject || '(No subject)',
                    messageId: lastEmail.messageId
                  };
                }
              }
              
              // Show desktop notification for batch (shows last email info)
              if (hasNewUnreadEmail && lastEmailData) {
                const body = newInboxEmailsCount > 1 
                  ? `${lastEmailData.subject} (+${newInboxEmailsCount - 1} more)`
                  : lastEmailData.subject;
                  
                desktopNotifications.showEmailNotification({
                  senderName: lastEmailData.fromName,
                  senderEmail: lastEmailData.fromEmail,
                  subject: body,
                  emailId: lastEmailData.messageId,
                });
              }
              
              // Invalidate memory cache timestamp for affected folders (but keep the data we just added)
              // The next folder navigation will get fresh data from server
              const affectedCacheKeys = new Set(
                emailsToAdd.map((emailData) => {
                  const folder = emailData.folder || 'INBOX';
                  const mailboxId = normalizeEventMailboxId(emailData.accountId);
                  const cacheAccountKey = getCacheAccountKeyForMailboxEvent(mailboxId);
                  return `${folder}:${cacheAccountKey}`;
                })
              );
              affectedCacheKeys.forEach(cacheKey => {
                set((state) => ({
                  folderThreadsLastSync: { ...state.folderThreadsLastSync, [cacheKey]: 0 }
                }));
                console.log(`[EmailStore] Invalidated cache timestamp for ${cacheKey} (data preserved)`);
              });
              
              // NOTE: We no longer do a background refresh immediately after adding new emails.
              // The instant-added threads are now visible, and doing a fetchThreads could overwrite
              // them if MongoDB hasn't indexed the new email yet (eventual consistency).
              // The cache is invalidated, so next folder switch will get fresh data.
              // However, we schedule a delayed background refresh so that full
              // thread data (attachments, decrypted bodies, security analysis)
              // is eventually loaded without requiring a manual refresh.
              setTimeout(() => {
                const currentState = get();
                if (currentState.threadsSearchQuery) return;
                console.log('[EmailStore] Delayed background refresh after new_emails event');
                void currentState.backgroundRefreshThreads(currentState.selectedFolder || 'INBOX');
              }, 3000);
            }
            // For single email (new_email action)
            else if (event.email) {
              const folder = event.email.folder || 'INBOX';
              const isUnread = !(event.email.is_read ?? event.email.read ?? false);
              const fromName = getEventFromName(event.email.from);
              const fromEmail = getEventFromAddress(event.email.from);
              const mailboxId = normalizeEventMailboxId(event.email.accountId);
              
              // Track unread inbox emails for badge
              if (folder === 'INBOX' && isUnread) {
                incrementUnreadCountForMailboxEvent(mailboxId, 1);
                hasNewUnreadEmail = true;
              }
              
              // Add to visible list if viewing this folder
              if (folder === selectedFolder && eventBelongsToCurrentView(mailboxId)) {
                // Add to threads state
                const threadId = event.email.threadId || event.email.thread_id || event.email.messageId;
                const existingThread = get().threads.find((t: ThreadListItem) => 
                  t.thread_id === threadId || t.thread_id === event.email!.messageId
                );
                
                // Normalize from object - WebSocket sends {address, name}, but ThreadListItem expects {email, name, address}
                let fromObj: { email: string; name: string; address?: string };
                if (typeof event.email.from === 'string') {
                  fromObj = { email: event.email.from, name: event.email.from.split('@')[0], address: event.email.from };
                } else if (event.email.from) {
                  const fromEml = event.email.from.address || event.email.from.email || 'unknown';
                  const fromNm = event.email.from.name || fromEml.split('@')[0];
                  fromObj = { email: fromEml, name: fromNm, address: fromEml };
                } else {
                  fromObj = { email: 'unknown', name: 'Unknown', address: 'unknown' };
                }
                
                if (!existingThread) {
                  const newThread: ThreadListItem = {
                    thread_id: threadId,
                    subject: event.email.subject || '(No subject)',
                    participants: [fromObj],
                    last_message_at: event.email.date || new Date().toISOString(),
                    last_message_preview: event.email.snippet || '',
                    last_message_from: fromObj,
                    folder: folder,
                    labels: [],
                    has_unread: isUnread,
                    unread_count: isUnread ? 1 : 0,
                    starred: false,
                    message_count: 1,
                  };
                  
                  const cacheKey = `${folder}:${get().activeSharedMailboxId || 0}`;
                  set((state) => {
                    const existingThreads = state.threads;
                    const existingCached = state.folderThreads[cacheKey] || [];
                    
                    console.log(`[EmailStore] Adding single new thread instantly: ${threadId}`);

                    // Skip the visible list while a server-side search is
                    // active — the thread lands in the folder cache and shows
                    // once the search is cleared.
                    return {
                      ...(state.threadsSearchQuery ? {} : { threads: [newThread, ...existingThreads] }),
                      folderThreads: { ...state.folderThreads, [cacheKey]: [newThread, ...existingCached] },
                    };
                  });
                } else {
                  // Update existing thread with new message data (reply in existing thread)
                  const cacheKey = `${folder}:${get().activeSharedMailboxId || 0}`;
                  set((state) => {
                    const updateThread = (t: ThreadListItem) => {
                      if (t.thread_id !== existingThread.thread_id) return t;
                      return {
                        ...t,
                        last_message_at: event.email!.date || new Date().toISOString(),
                        last_message_preview: (event.email as any)?.snippet || t.last_message_preview,
                        last_message_from: fromObj,
                        has_unread: isUnread || t.has_unread,
                        unread_count: (t.unread_count || 0) + (isUnread ? 1 : 0),
                        message_count: (t.message_count || 1) + 1,
                      };
                    };
                    
                    console.log(`[EmailStore] Updating existing thread with new message: ${existingThread.thread_id}`);
                    
                    return {
                      threads: state.threads.map(updateThread),
                      folderThreads: { ...state.folderThreads, [cacheKey]: (state.folderThreads[cacheKey] || []).map(updateThread) },
                    };
                  });
                }
              }
              
              // Show desktop notification for new unread emails
              if (hasNewUnreadEmail) {
                desktopNotifications.showEmailNotification({
                  senderName: fromName,
                  senderEmail: fromEmail,
                  subject: event.email.subject || '(No subject)',
                  emailId: event.email.messageId,
                });
              }
              
              // Invalidate memory cache timestamp for affected folder
              const cacheKey = `${folder}:${getCacheAccountKeyForMailboxEvent(mailboxId)}`;
              set((state) => ({
                folderThreadsLastSync: { ...state.folderThreadsLastSync, [cacheKey]: 0 }
              }));
              console.log(`[EmailStore] Invalidated cache timestamp for ${cacheKey} (data preserved)`);
              
              // NOTE: We no longer do a background refresh - the instant-added thread is now visible.
              // Doing a fetchThreads could overwrite it due to MongoDB eventual consistency.
              // Cache is invalidated, so next folder switch will get fresh data.
              // However, we schedule a delayed background refresh so that full
              // thread data (attachments, decrypted bodies, security analysis)
              // is eventually loaded without requiring a manual refresh.
              setTimeout(() => {
                const currentState = get();
                if (currentState.threadsSearchQuery) return;
                console.log('[EmailStore] Delayed background refresh after new_email event');
                void currentState.backgroundRefreshThreads(currentState.selectedFolder || 'INBOX');
              }, 3000);
            }
            break;
          
          case 'internal_email_sent':
            // Handle when sender's email was delivered internally
            // This notifies the sender that their email is now in the Sent folder
            console.log('[EmailStore] Internal email sent notification received:', event);
            
            // Invalidate Sent folder cache so next view shows the sent email
            {
              const sentAccountId = get().activeSharedMailboxId || 0;
              const sentCacheKey = `Sent:${sentAccountId}`;
              set((state) => ({
                folderThreadsLastSync: { ...state.folderThreadsLastSync, [sentCacheKey]: 0 }
              }));
              console.log(`[EmailStore] Invalidated Sent folder cache after internal send`);
            }
            break;
          
          case 'email_moved':
            // Handle email moved from one folder to another (e.g., Outbox -> Sent after successful delivery)
            if (event.email?.messageId) {
              const messageId = event.email.messageId;
              const fromFolder = event.email.fromFolder || 'Outbox';
              const toFolder = event.email.toFolder || 'Sent';
              const threadId = event.email.threadId; // NEW: Get threadId from broadcast
              const lastMessageFolder = event.email.last_message_folder; // NEW: Track last message folder
              const isReply = event.email.isReply; // NEW: Flag indicating this was a reply
              const accountId = get().activeSharedMailboxId || 0;
              const currentFolder = get().selectedFolder;
              
              console.log(`[EmailStore] Email moved: ${messageId} from ${fromFolder} to ${toFolder}, thread=${threadId}, lastMsgFolder=${lastMessageFolder}, isReply=${isReply}`);
              
              // Update thread folder in state + folderThreads cache
              const fromCacheKeyWs = `${fromFolder}:${accountId}`;
              set((state) => {
                // Update threads array - find thread containing this message and update its folder
                const updatedThreads = state.threads.map((t: ThreadListItem) => {
                  // Match by threadId (preferred) or by messageId/from address
                  const threadSenderAddress = (t.last_message_from as any)?.address || t.last_message_from?.email;
                  const eventSenderAddress = getEventFromAddress(event.email?.from);
                  if (t.thread_id === threadId || t.thread_id === messageId || threadSenderAddress === eventSenderAddress) {
                    return { 
                      ...t, 
                      folder: toFolder,
                      last_message_folder: lastMessageFolder, // Track last message folder for INBOX filtering
                      last_sent_at: new Date().toISOString() // Update sent timestamp
                    };
                  }
                  return t;
                });
                
                // Also remove from source folder's folderThreads cache
                const updatedFolderThreads = { ...state.folderThreads };
                if (updatedFolderThreads[fromCacheKeyWs] && threadId) {
                  updatedFolderThreads[fromCacheKeyWs] = updatedFolderThreads[fromCacheKeyWs].filter(
                    (t: ThreadListItem) => t.thread_id !== threadId && t.thread_id !== messageId
                  );
                }
                
                return { threads: updatedThreads, folderThreads: updatedFolderThreads };
              });
              
              // CRITICAL FIX: If this was a reply and we're viewing INBOX, remove the thread
              // because the last message is now Sent (not received), so it shouldn't show in INBOX
              if (isReply && threadId && (currentFolder === 'INBOX' || currentFolder?.toLowerCase() === 'inbox')) {
                const inboxKey = `INBOX:${accountId}`;
                set((state) => {
                  const updatedFolderThreads = { ...state.folderThreads };
                  if (updatedFolderThreads[inboxKey]) {
                    updatedFolderThreads[inboxKey] = updatedFolderThreads[inboxKey].filter(
                      (t: ThreadListItem) => t.thread_id !== threadId
                    );
                  }
                  return {
                    threads: state.threads.filter((t: ThreadListItem) => t.thread_id !== threadId),
                    selectedThread: state.selectedThread?.thread_id === threadId ? null : state.selectedThread,
                    folderThreads: updatedFolderThreads,
                  };
                });
                console.log(`[EmailStore] Removed thread ${threadId} from INBOX view (reply sent, last_message_folder=${lastMessageFolder})`);
              }
              
              // Update IndexedDB cache: move thread to Sent folder with updated metadata
              if (isReply && threadId) {
                emailCacheService.moveThreadToFolder(threadId, 'Sent', accountId, {
                  last_message_folder: 'Sent',
                  last_sent_at: new Date().toISOString()
                }).catch(err => {
                  console.error('[EmailStore] Failed to update IndexedDB thread cache:', err);
                });
              }
              
              // Invalidate both source and destination folder caches
              // Also invalidate INBOX cache if this was a reply (thread moves out of INBOX)
              const fromCacheKey = `${fromFolder}:${accountId}`;
              const toCacheKey = `${toFolder}:${accountId}`;
              const inboxCacheKey = `INBOX:${accountId}`;
              const cacheInvalidations: Record<string, number> = {
                [fromCacheKey]: 0,
                [toCacheKey]: 0 
              };
              // If this was a reply, also invalidate INBOX cache since thread may have moved out
              if (isReply) {
                cacheInvalidations[inboxCacheKey] = 0;
              }
              set((state) => ({
                folderThreadsLastSync: { 
                  ...state.folderThreadsLastSync, 
                  ...cacheInvalidations
                }
              }));
              console.log(`[EmailStore] Invalidated caches for ${fromFolder}, ${toFolder}${isReply ? ', and INBOX (reply)' : ''}`);
            }
            break;
          
          case 'thread_moved':
            // Handle thread moved by another delegate on a shared mailbox
            if (event.thread_id) {
              const threadId = event.thread_id;
              const fromFolder = event.fromFolder || 'INBOX';
              const toFolder = event.toFolder || 'INBOX';
              const accountId = get().activeSharedMailboxId || 0;
              const currentFolder = get().selectedFolder;
              
              console.log(`[EmailStore] Thread moved by delegate: ${threadId} from ${fromFolder} to ${toFolder}`);
              
              // Update thread folder in state and remove from source folder cache
              const fromCacheKeyDel = `${fromFolder}:${accountId}`;
              set((state) => {
                // Update display threads
                const updatedThreads = state.threads.map((t: ThreadListItem) => 
                  t.thread_id === threadId ? { ...t, folder: toFolder } : t
                );
                
                // Remove thread from source folder cache, clear destination cache
                const updatedFolderThreads = { ...state.folderThreads };
                if (updatedFolderThreads[fromCacheKeyDel]) {
                  updatedFolderThreads[fromCacheKeyDel] = updatedFolderThreads[fromCacheKeyDel].filter(
                    (t: ThreadListItem) => t.thread_id !== threadId
                  );
                }
                // Clear destination folder cache to force re-fetch
                const toCacheKeyDel = `${toFolder}:${accountId}`;
                delete updatedFolderThreads[toCacheKeyDel];
                
                return { threads: updatedThreads, folderThreads: updatedFolderThreads };
              });
              
              // If viewing the source folder, also remove from display threads
              if (currentFolder === fromFolder || currentFolder?.toLowerCase() === fromFolder?.toLowerCase()) {
                set((state) => ({
                  threads: state.threads.filter((t: ThreadListItem) => t.thread_id !== threadId),
                  selectedThread: state.selectedThread?.thread_id === threadId ? null : state.selectedThread,
                }));
                console.log(`[EmailStore] Removed thread ${threadId} from ${fromFolder} view`);
              }
              
              // Invalidate both folder caches
              const fromCacheKey = `${fromFolder}:${accountId}`;
              const toCacheKey = `${toFolder}:${accountId}`;
              set((state) => ({
                folderThreadsLastSync: { 
                  ...state.folderThreadsLastSync, 
                  [fromCacheKey]: 0,
                  [toCacheKey]: 0 
                }
              }));
              console.log(`[EmailStore] Invalidated caches for ${fromFolder} and ${toFolder} after delegate move`);
            }
            break;
            
          case 'email_updated':
            // Email-level updates are now handled by thread-level operations
            break;
            
          case 'thread_read_status_changed':
            // Handle real-time read status changes for shared mailbox threads
            // This updates the delegate avatars when another user marks an email as read
            console.log('[EmailStore] Thread read status event received:', {
              full_event: event,
              has_thread_id: !!event.thread_id,
              has_read_by_users: !!event.read_by_users,
              event_keys: Object.keys(event)
            });
            
            if (event.thread_id && event.read_by_users) {
              console.log('[EmailStore] Processing thread read status change:', {
                thread_id: event.thread_id,
                read_by_users: event.read_by_users,
                changed_by: event.changed_by_user_id,
                current_user: get().currentUserId
              });
              
              const currentUserId = get().currentUserId;
              const isReadByCurrentUser = currentUserId ? event.read_by_users.includes(currentUserId) : false;
              const mailboxId = normalizeEventMailboxId(event.mailbox_id);
              const previousThread = get().threads.find((thread) => thread.thread_id === event.thread_id)
                || Object.values(get().folderThreads).flat().find((thread) => thread.thread_id === event.thread_id);
              const wasUnreadForCurrentUser = previousThread ? previousThread.has_unread : null;
              
              // Update thread in all caches
              set((state) => {
                const updateThread = (t: ThreadListItem) => {
                  if (t.thread_id !== event.thread_id) return t;
                  
                  return {
                    ...t,
                    read_by_users: event.read_by_users,
                    has_unread: !isReadByCurrentUser,
                    unread_count: isReadByCurrentUser ? 0 : 1,
                  };
                };
                
                // Update main threads array
                const updatedThreads = state.threads.map(updateThread);
                
                // Update all folder caches
                const updatedFolderThreads: Record<string, ThreadListItem[]> = {};
                Object.keys(state.folderThreads).forEach(folderKey => {
                  updatedFolderThreads[folderKey] = state.folderThreads[folderKey].map(updateThread);
                });
                
                // Update selected thread if it matches
                const updatedSelectedThread = state.selectedThread && state.selectedThread.thread_id === event.thread_id
                  ? updateThread(state.selectedThread)
                  : state.selectedThread;
                
                return {
                  threads: updatedThreads,
                  folderThreads: updatedFolderThreads,
                  selectedThread: updatedSelectedThread,
                };
              });
              
              // Update IndexedDB cache (background, non-blocking)
              const activeSharedMailboxId = get().activeSharedMailboxId;
              const selectedFolder = get().selectedFolder;
              const updatedThreads = get().threads;
              emailCacheService.storeThreads(updatedThreads, selectedFolder, activeSharedMailboxId || 0)
                .catch(e => console.log('[EmailStore] IndexedDB cache update error (non-fatal):', e));

              if (mailboxId && isSharedMailboxEvent(mailboxId) && wasUnreadForCurrentUser !== null && wasUnreadForCurrentUser !== !isReadByCurrentUser) {
                set((state) => ({
                  sharedMailboxUnreadCounts: {
                    ...state.sharedMailboxUnreadCounts,
                    [mailboxId]: Math.max(
                      0,
                      (state.sharedMailboxUnreadCounts[mailboxId] || 0) + (isReadByCurrentUser ? -1 : 1)
                    ),
                  },
                }));
              }
            }
            break;
        }
        break;
        
      case 'email_sync_status':
        set((state) => ({
          syncStatus: {
            ...state.syncStatus,
            syncing: event.syncing ?? false,
            lastSync: event.last_sync ? new Date(event.last_sync) : state.syncStatus.lastSync
          }
        }));
        
        // If sync completed with new emails, refresh the list
        if (!event.syncing && event.new_email_count && event.new_email_count > 0) {
          get().fetchEmails(get().selectedFolder);
        }
        break;
        
      case 'email_sync_error':
        console.error('[EmailStore] Sync error:', event.error);
        set((state) => ({
          syncStatus: { ...state.syncStatus, syncing: false }
        }));
        break;
      
      case 'email_queue_update':
        // Handle email queue status updates (sent/failed from background processing)
        console.log('[EmailStore] Queue update:', event);
        if (event.queueId) {
          const outboxItem = get().outboxItems.find(
            (item) => item.queueId === event.queueId || item.tempId === event.queueId
          );
          
          if (outboxItem) {
            if (event.status === 'sent') {
              get().markOutboxItemSent(outboxItem.tempId, event.messageId);
            } else if (event.status === 'failed') {
              get().markOutboxItemFailed(outboxItem.tempId, event.error || 'Failed to send email');
            } else if (event.status === 'retrying') {
              get().updateOutboxItem(outboxItem.tempId, {
                status: 'sending',
                error: `Retrying (attempt ${event.retryCount})...`,
              });
            }
          }
        }
        break;
      
      case 'security_update':
        // Handle security status update from reanalysis or contact changes
        console.log('[EmailStore] Security update:', event);
        if (event.threadId) {
          // Invalidate any cached security analysis for this thread
          // The next fetch of this thread will get fresh security data
          set((state) => {
            // Find and update the thread's security status
            const updateThreadSecurity = (t: ThreadListItem) => {
              if (t.thread_id !== event.threadId) return t;
              return {
                ...t,
                deep_analysis_available: true,
                deep_analysis_at: event.timestamp,
                // Force refresh security data when thread is viewed again
                _securityRefreshNeeded: true,
              };
            };
            
            const updatedThreads = state.threads.map(updateThreadSecurity);
            const updatedFolderThreads: Record<string, ThreadListItem[]> = {};
            Object.keys(state.folderThreads).forEach(folderKey => {
              updatedFolderThreads[folderKey] = state.folderThreads[folderKey].map(updateThreadSecurity);
            });
            
            // If viewing this thread, update selected thread too
            const updatedSelectedThread = state.selectedThread?.thread_id === event.threadId
              ? updateThreadSecurity(state.selectedThread as ThreadListItem)
              : state.selectedThread;
            
            return {
              threads: updatedThreads,
              folderThreads: updatedFolderThreads,
              selectedThread: updatedSelectedThread,
            };
          });
          
          // Log security update action
          console.log(`[EmailStore] 🛡️ Security updated for thread ${event.threadId}: ${event.action}`);
        }
        break;
        
      case 'email_subscribed':
        console.log('[EmailStore] WebSocket subscribed to email sync');
        break;
    }
  },
  
  // ============================================================================
  // EMAIL THREADS (MongoDB-based unified messaging)
  // ============================================================================
  
  selectThread: (thread: ThreadListItem | null) => {
    set({ selectedThread: thread });
    
    // Mark as read when opened
    if (thread && thread.has_unread) {
      get().markThreadRead(thread.thread_id);
    }
  },
  
  fetchThreads: async (options?: { folder?: string; unread?: boolean; starred?: boolean; search?: string; accountId?: number; includeSharedMailboxThreads?: boolean; forceRefresh?: boolean; append?: boolean }) => {
    const folder = options?.folder || get().selectedFolder;
    // Use provided accountId, or fall back to activeSharedMailboxId from store
    const accountIdToUse = options?.accountId !== undefined ? options.accountId : get().activeSharedMailboxId;
    const includeSharedMailboxThreads = options?.includeSharedMailboxThreads !== undefined
      ? options.includeSharedMailboxThreads
      : (get().includeSharedMailboxThreads && !accountIdToUse);
    const forceRefresh = options?.forceRefresh || false;
    const append = options?.append || false;
    const hasExplicitSearch = !!options && Object.prototype.hasOwnProperty.call(options, 'search');
    const shouldPreserveActiveSearch = !hasExplicitSearch && (!options?.folder || options.folder === get().selectedFolder);
    const searchQuery = hasExplicitSearch ? (options?.search || '') : (shouldPreserveActiveSearch ? get().threadsSearchQuery : '');
    
    console.log('[EmailStore] fetchThreads called with folder:', folder, 'options:', options, 'accountIdToUse:', accountIdToUse);
    
    // Reset pagination if not appending (new search or folder change)
    if (!append) {
      set({ threadsCurrentSkip: 0, threadsSearchQuery: searchQuery });
    }
    
    // Check if user is offline
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
    
    // When searching, skip cache - always fetch from server (unless offline)
    const isSearching = searchQuery && searchQuery.trim().length >= 2;

    // Stale-response guard: while a request is in flight the visible view can
    // move on (search typed/cleared or a folder switch). A response that no
    // longer matches the current view must not overwrite `threads`; its data
    // is still stored under its own folder cache key.
    const isStaleThreadView = () => {
      const stateNow = get();
      return (
        stateNow.threadsSearchQuery.trim() !== searchQuery.trim() ||
        stateNow.selectedFolder !== folder
      );
    };
    
    // Check if we have recent memory cache (skip server fetch if synced recently)
    // SKIP cache when searching - always use server search
    const cacheAccountId = getThreadScopeCacheAccountId(accountIdToUse, includeSharedMailboxThreads);
    const folderCacheKey = getThreadScopeCacheKey(folder, accountIdToUse, includeSharedMailboxThreads);
    const lastSyncTime = get().folderThreadsLastSync[folderCacheKey] || 0;
    const timeSinceSync = Date.now() - lastSyncTime;
    const CACHE_FRESH_THRESHOLD = 300000; // 5 minutes - allows navigating through all folders without refetch
    
    // Use memory cache if available and not forcing refresh AND NOT SEARCHING
    // CRITICAL: Use folderCacheKey (folder:accountId) to prevent mixing personal and shared mailbox threads
    const memoryCachedThreads = get().folderThreads[folderCacheKey];
    if (!isSearching && !forceRefresh && !append && memoryCachedThreads && memoryCachedThreads.length > 0 && timeSinceSync < CACHE_FRESH_THRESHOLD) {
      console.log('[EmailStore] Using fresh memory cache for', folderCacheKey, '- synced', Math.round(timeSinceSync/1000), 's ago');
      // Just update the display, no server fetch needed
      set({ threads: memoryCachedThreads, threadsLoading: false });
      return;
    }
    
    // IMPORTANT: Preserve currently displayed threads to prevent flash
    // selectFolder already set the threads from memory cache - don't overwrite
    const currentlyDisplayedThreads = get().threads;
    const hasVisibleData = currentlyDisplayedThreads.length > 0;
    
    // Try to load from IndexedDB cache for display while fetching
    try {
      // If we don't have memory cache AND no visible data, try IndexedDB
      // (skip when searching — unfiltered cache must not flash during a search)
      if (!isSearching && (!memoryCachedThreads || memoryCachedThreads.length === 0) && !hasVisibleData) {
        const cachedThreads = await emailCacheService.getThreads(folder, cacheAccountId);
        if (cachedThreads.length > 0) {
          console.log('[EmailStore] Showing IndexedDB cached threads:', cachedThreads.length);
          // Show cached data, DON'T show loading indicator since we have data
          if (!isStaleThreadView()) {
            set({ threads: cachedThreads, threadsLoading: false });
          }
          // Also update memory cache
          set((state) => ({
            folderThreads: { ...state.folderThreads, [folderCacheKey]: cachedThreads }
          }));
        } else {
          // No cache at all - show loading
          set({ threadsLoading: true, error: null });
        }
      } else if (memoryCachedThreads && memoryCachedThreads.length > 0) {
        // We have memory cache but it's stale - DON'T show loading, just background refresh
        console.log('[EmailStore] Memory cache stale, background refreshing (no loading indicator)');
        // Don't set threads again - selectFolder already did that
      } else if (hasVisibleData) {
        // We have visible data from selectFolder - keep showing it
        console.log('[EmailStore] Keeping visible data, background refreshing');
      }
    } catch (cacheError) {
      console.log('[EmailStore] Cache read error:', cacheError);
      // Only show loading if we don't already have threads displayed
      if (get().threads.length === 0) {
        set({ threadsLoading: true, error: null });
      }
    }
    
    try {
      const currentSkip = append ? get().threadsCurrentSkip : 0;
      const limit = 0; // 0 = fetch ALL threads (no pagination)
      
      // If offline and searching, use local cache with client-side filtering
      if (isOffline && isSearching) {
        console.log('🔌 [fetchThreads] OFFLINE MODE - searching local cache for:', searchQuery);
        
        try {
          // Get all cached threads for this folder
          const allCachedThreads = await emailCacheService.getThreads(folder, cacheAccountId, { limit: 1000 });
          
          // Filter locally using same logic as server search
          const searchLower = searchQuery.toLowerCase();
          const filteredThreads = allCachedThreads.filter((thread: any) => {
            // Search in subject
            if (thread.subject?.toLowerCase().includes(searchLower)) return true;
            
            // Search in participants (email and name)
            if (thread.participants?.some((p: any) => 
              p.address?.toLowerCase().includes(searchLower) || 
              p.name?.toLowerCase().includes(searchLower)
            )) return true;
            
            // Search in last message preview
            if (thread.last_message_preview?.toLowerCase().includes(searchLower)) return true;
            
            // Search in message bodies if available
            if (thread.messages?.some((m: any) => 
              m.body_text?.toLowerCase().includes(searchLower) ||
              m.snippet?.toLowerCase().includes(searchLower)
            )) return true;
            
            return false;
          });
          
          console.log(`🔌 [fetchThreads] OFFLINE search found ${filteredThreads.length} matches in cache`);
          
          // Return all filtered results (no pagination)
          const threads = append ? [...get().threads, ...filteredThreads] : filteredThreads;

          if (isStaleThreadView()) return;

          set({
            threads,
            threadsLoading: false,
            threadsCurrentSkip: 0,
            threadsHasMore: false,
            threadsTotalCount: filteredThreads.length
          });

          return; // Exit early - we've handled offline search
        } catch (offlineError) {
          console.error('🔌 [fetchThreads] Offline search failed:', offlineError);
          set({ 
            threadsLoading: false,
            error: 'You are offline and local search failed. Please try again when online.' 
          });
          return;
        }
      }
      
      const response = await emailApiService.getThreads({
        folder,
        unread: options?.unread,
        starred: options?.starred,
        search: searchQuery || undefined, // Use server-side search
        accountId: accountIdToUse || undefined, // Pass shared mailbox ID to filter by account
        includeSharedMailboxes: includeSharedMailboxThreads,
        limit,
        skip: currentSkip,
      });
      
      // Handle both response formats: { success, data: { threads } } or { success, threads }
      const threadsData = (response as any).threads || response.data?.threads || [];
      const totalCount = (response as any).count || (response.data?.count) || threadsData.length;
      
      console.log('[EmailStore] fetchThreads response:', {
        success: response.success,
        threadCount: threadsData.length,
        totalCount,
        skip: currentSkip,
        limit,
        folder,
        search: searchQuery,
        threadIds: threadsData.slice(0, 5).map((t: any) => t.thread_id)
      });
      
      if (response.success && threadsData.length > 0) {
        const newThreads: ThreadListItem[] = threadsData.map((t: any) => mapThreadSummary(t));
        
        // Combine with existing threads if appending (Load More)
        const threads = append ? [...get().threads, ...newThreads] : newThreads;
        
        console.log('[EmailStore] Loaded threads:', {
          append,
          currentThreads: get().threads.length,
          newThreads: newThreads.length,
          totalThreads: threads.length,
          totalCount,
        });
        
        // Update display and memory cache (only cache non-search results)
        if (!isSearching) {
          console.log(`📧 [fetchThreads] Saving ${threads.length} threads to memory cache for "${folderCacheKey}"`);
          const staleView = isStaleThreadView();
          if (staleView) {
            console.log(`📧 [fetchThreads] Response for "${folderCacheKey}" arrived after the view moved on - caching only`);
          }
          set((state) => ({
            ...(staleView ? {} : {
              threads,
              threadsLoading: false,
              threadsCurrentSkip: 0,
              threadsHasMore: false,
              threadsTotalCount: totalCount,
            }),
            folderThreads: { ...state.folderThreads, [folderCacheKey]: threads },
            folderThreadsLastSync: { ...state.folderThreadsLastSync, [folderCacheKey]: Date.now() }
          }));

          // Verify the cache was saved
          const savedCache = get().folderThreads[folderCacheKey];
          console.log(`📧 [fetchThreads] Verified cache for "${folderCacheKey}": ${savedCache?.length ?? 0} threads`);
        } else {
          // When searching, don't cache but update state
          if (isStaleThreadView()) {
            console.log(`🔍 [fetchThreads] Discarding stale search results for "${searchQuery}" - view moved on`);
          } else {
            console.log(`🔍 [fetchThreads] Search results (not cached): ${threads.length} threads`);
            set({
              threads,
              threadsLoading: false,
              threadsCurrentSkip: 0,
              threadsHasMore: false,
              threadsTotalCount: totalCount
            });
          }
        }
        
        // Update inbox unread count from threads
        if (folder === 'INBOX') {
          const unreadCount = threads.filter(t => t.has_unread).length;
          set({ inboxUnreadCount: unreadCount });
        }
        
        // Update spam unread count from threads (check both Spam and Junk folder names)
        if (folder === 'Spam' || folder === 'Junk' || folder === 'spam') {
          const unreadCount = threads.filter(t => t.has_unread).length;
          set({ spamUnreadCount: unreadCount });
        }
        
        // Cache threads to IndexedDB for offline access (background, non-blocking)
        // Only cache non-search results
        if (!isSearching) {
          emailCacheService.storeThreads(threads, folder, cacheAccountId)
            .then(() => console.log('[EmailStore] Threads cached to IndexedDB'))
            .catch(e => console.log('[EmailStore] IndexedDB cache write error (non-fatal):', e));
        }
          
      } else if (response.success && threadsData.length === 0) {
        // No threads in folder or no more threads to load
        if (append) {
          // No more threads to load
          console.log('[EmailStore] No more threads to load - end of list');
          if (!isStaleThreadView()) {
            set({ threadsLoading: false, threadsHasMore: false });
          }
        } else if (isSearching) {
          // Search returned no results - don't poison the normal folder cache
          if (isStaleThreadView()) {
            console.log('[EmailStore] Discarding stale empty search result for query:', searchQuery);
          } else {
            console.log('[EmailStore] Search returned no results for query:', searchQuery);
            set({
              threads: [],
              threadsLoading: false,
              threadsCurrentSkip: 0,
              threadsHasMore: false,
              threadsTotalCount: 0,
              error: null,
            });
          }
        } else {
          // Folder is empty
          const staleView = isStaleThreadView();
          set((state) => ({
            ...(staleView ? {} : {
              threads: [],
              threadsLoading: false,
              threadsHasMore: false,
            }),
            folderThreads: { ...state.folderThreads, [folderCacheKey]: [] },
            folderThreadsLastSync: { ...state.folderThreadsLastSync, [folderCacheKey]: Date.now() }
          }));
          if (folder === 'INBOX') {
            set({ inboxUnreadCount: 0 });
          }
          // Reset spam count when spam folder is empty
          if (folder === 'Spam' || folder === 'Junk' || folder === 'spam') {
            set({ spamUnreadCount: 0 });
          }
          // Clear IndexedDB cache for this folder
          emailCacheService.clearThreads(folder, cacheAccountId).catch(() => {});
        }
      } else {
        // Server returned an error — try showing cached threads instead of a dead screen
        try {
          const cachedThreads = await emailCacheService.getThreads(folder, cacheAccountId, { limit: 1000 });
          if (cachedThreads.length > 0) {
            if (!isStaleThreadView()) {
              console.log(`📦 [fetchThreads] Server error, showing ${cachedThreads.length} cached threads instead`);
              set({ threads: cachedThreads, threadsLoading: false, error: null });
            }
            set((state) => ({
              folderThreads: { ...state.folderThreads, [folderCacheKey]: cachedThreads }
            }));
            return;
          }
        } catch (_) { /* IndexedDB also failed, show original error */ }
        if (!isStaleThreadView()) {
          set({ error: response.error || 'Failed to fetch threads', threadsLoading: false });
        }
      }
    } catch (error: any) {
      console.error('[EmailStore] Error fetching threads:', error);
      
      // If we failed due to network error and we're searching, try offline fallback
      if (isSearching && (error?.message?.includes('fetch') || error?.message?.includes('network') || !navigator.onLine)) {
        console.log('🔌 [fetchThreads] Network error detected - trying offline search fallback');
        
        try {
          const allCachedThreads = await emailCacheService.getThreads(folder, cacheAccountId, { limit: 1000 });
          const searchLower = searchQuery.toLowerCase();
          const filteredThreads = allCachedThreads.filter((thread: any) => {
            return thread.subject?.toLowerCase().includes(searchLower) ||
              thread.participants?.some((p: any) => 
                p.address?.toLowerCase().includes(searchLower) || 
                p.name?.toLowerCase().includes(searchLower)
              ) ||
              thread.last_message_preview?.toLowerCase().includes(searchLower) ||
              thread.messages?.some((m: any) => 
                m.body_text?.toLowerCase().includes(searchLower) ||
                m.snippet?.toLowerCase().includes(searchLower)
              );
          });
          
          console.log(`🔌 [fetchThreads] Offline fallback found ${filteredThreads.length} matches`);
          
          const threads = append ? [...get().threads, ...filteredThreads] : filteredThreads;
          
          set({ 
            threads,
            threadsLoading: false,
            threadsCurrentSkip: 0,
            threadsHasMore: false,
            threadsTotalCount: filteredThreads.length,
            error: 'Showing offline results from local cache'
          });
          return;
        } catch (offlineError) {
          console.error('🔌 [fetchThreads] Offline fallback also failed:', offlineError);
          set({ threadsLoading: false, error: 'Network error and offline search failed' });
          return;
        }
      }
      
      // Auto-retry once after 3s for transient server errors (e.g. websocket service restart)
      const retryCount = (options as any)?._retryCount || 0;
      if (retryCount < 1 && (error.message?.includes('fetch') || error.message?.includes('ECONNREFUSED') || error.message?.includes('Failed to fetch'))) {
        console.log(`[EmailStore] Auto-retrying fetchThreads in 3s (attempt ${retryCount + 1})`);
        set({ error: 'Connection lost, retrying...', threadsLoading: true });
        setTimeout(() => {
          get().fetchThreads({ ...options, _retryCount: retryCount + 1 } as any);
        }, 3000);
        return;
      }
      
      // Fall back to IndexedDB cached threads so user sees their previously downloaded emails
      try {
        const cachedThreads = await emailCacheService.getThreads(folder, cacheAccountId, { limit: 1000 });
        if (cachedThreads.length > 0) {
          const staleView = isStaleThreadView();
          if (!staleView) {
            console.log(`📦 [fetchThreads] Showing ${cachedThreads.length} cached threads from IndexedDB after fetch failure`);
          }
          const threads = append ? [...get().threads, ...cachedThreads] : cachedThreads;
          set({
            ...(staleView ? {} : {
              threads,
              threadsLoading: false,
              threadsCurrentSkip: 0,
              threadsHasMore: false,
              threadsTotalCount: cachedThreads.length,
              error: null // Don't show error — user sees their cached data seamlessly
            })
          });
          // Also populate memory cache so folder switching stays fast
          set((state) => ({
            folderThreads: { ...state.folderThreads, [folderCacheKey]: threads }
          }));
          return;
        }
      } catch (cacheErr) {
        console.error('[fetchThreads] IndexedDB fallback also failed:', cacheErr);
      }

      if (!isStaleThreadView()) {
        set({ error: error.message || 'Failed to fetch threads', threadsLoading: false });
      }
    }
  },
  
  /**
   * Load more threads (pagination - appends to existing threads)
   */
  loadMoreThreads: async () => {
    const currentFolder = get().selectedFolder;
    const hasMore = get().threadsHasMore;
    const isLoading = get().threadsLoading;
    const searchQuery = get().threadsSearchQuery;
    
    if (!hasMore || isLoading) {
      console.log('[EmailStore] loadMoreThreads - nothing to load', { hasMore, isLoading });
      return;
    }
    
    console.log('[EmailStore] loadMoreThreads - fetching next page');
    await get().fetchThreads({ 
      folder: currentFolder, 
      search: searchQuery || undefined,
      append: true // Append to existing threads
    });
  },
  
  /**
   * Set search query and trigger server-side search
   */
  setThreadsSearchQuery: (query: string) => {
    const trimmedQuery = query.trim();
    console.log('[EmailStore] setThreadsSearchQuery:', trimmedQuery);
    
    // Update search query state
    set({ threadsSearchQuery: trimmedQuery });
    
    if (trimmedQuery.length >= 2) {
      // Trigger server-side search
      get().fetchThreads({ 
        search: trimmedQuery,
        forceRefresh: true // Skip cache for search
      });
    } else if (trimmedQuery.length === 0) {
      // Clear search - refresh current folder
      get().fetchThreads({ forceRefresh: true, search: '' });
    }
  },
  
  /**
   * Smart sync - only fetches if server stats differ from local
   * Returns { synced: boolean, reason: string }
   */
  smartSyncThreads: async (folder?: string) => {
    const targetFolder = folder || get().selectedFolder;
    const currentThreads = get().threads;
    const activeSearchQuery = get().threadsSearchQuery;
    const accountIdToUse = get().activeSharedMailboxId;
    const includeSharedMailboxThreads = get().includeSharedMailboxThreads && !accountIdToUse;
    
    console.log('[EmailStore] smartSyncThreads - checking for changes in folder:', targetFolder);
    
    try {
      // Show syncing indicator but DON'T clear the thread list
      set({ syncStatus: { ...get().syncStatus, syncing: true } });

      if (activeSearchQuery) {
        console.log('[EmailStore] smartSyncThreads - refreshing active search instead of folder stats:', activeSearchQuery);
        await get().fetchThreads({
          folder: targetFolder,
          search: activeSearchQuery,
          includeSharedMailboxThreads,
          forceRefresh: true,
        });
        set({ syncStatus: { connected: true, syncing: false, lastSync: new Date() } });
        return { synced: true, reason: 'search_refresh' };
      }
      
      // Fetch current stats from server (lightweight call)
      const statsResponse = await emailApiService.getThreadStats({
        accountId: accountIdToUse || undefined,
        includeSharedMailboxes: includeSharedMailboxThreads,
      });
      
      if (!statsResponse.success || !statsResponse.data?.stats) {
        // Stats fetch failed - do a background refresh without clearing UI
        console.log('[EmailStore] smartSyncThreads - stats unavailable, doing background refresh');
        await get().backgroundRefreshThreads(targetFolder);
        set({ syncStatus: { connected: true, syncing: false, lastSync: new Date() } });
        return { synced: true, reason: 'stats_unavailable' };
      }
      
      const serverStats = statsResponse.data.stats;
      const folderStats = serverStats.folders?.[targetFolder];
      
      // Compare local thread count with server stats
      const localTotal = currentThreads.length;
      const serverTotal = getFolderStatsValue(folderStats, 'total') || serverStats.total_threads || 0;
      const serverUnread = getFolderStatsValue(folderStats, 'unread') || serverStats.unread_threads || 0;
      const localUnread = currentThreads.filter(t => t.has_unread).length;
      
      console.log('[EmailStore] smartSyncThreads - comparing:', {
        folder: targetFolder,
        localTotal,
        serverTotal,
        localUnread,
        serverUnread
      });
      
      // Check if we need to sync
      const needsSync = localTotal !== serverTotal || localUnread !== serverUnread || currentThreads.length === 0;
      
      if (!needsSync) {
        console.log('[EmailStore] smartSyncThreads - no changes detected, skipping full refresh');
        set({ 
          syncStatus: { connected: true, syncing: false, lastSync: new Date() },
          threadStats: serverStats
        });
        return { synced: false, reason: 'no_changes' };
      }
      
      // Stats differ - do a background refresh (keeps current UI while fetching)
      console.log('[EmailStore] smartSyncThreads - changes detected, background refreshing');
      await get().backgroundRefreshThreads(targetFolder);
      
      set({ 
        syncStatus: { connected: true, syncing: false, lastSync: new Date() },
        threadStats: serverStats
      });
      
      return { synced: true, reason: 'changes_detected' };
      
    } catch (error: any) {
      console.error('[EmailStore] smartSyncThreads error:', error);
      set({ syncStatus: { ...get().syncStatus, syncing: false } });
      return { synced: false, reason: 'error' };
    }
  },

  /**
   * Background refresh - fetches threads without clearing the UI
   * Only updates if there are actual differences
   */
  backgroundRefreshThreads: async (folder?: string) => {
    const targetFolder = folder || get().selectedFolder;
    const accountIdToUse = get().activeSharedMailboxId;
    const includeSharedMailboxThreads = get().includeSharedMailboxThreads && !accountIdToUse;
    const cacheAccountId = getThreadScopeCacheAccountId(accountIdToUse, includeSharedMailboxThreads);
    const currentThreads = get().threads;
    const activeSearchQuery = get().threadsSearchQuery;

    // A search may start (or the folder may change) while the refresh request
    // is in flight — only overwrite `threads` if the response still matches the
    // visible view. Folder cache entries are still updated under their own key.
    const isStaleThreadView = () => {
      const stateNow = get();
      return (
        stateNow.threadsSearchQuery.trim().length > 0 ||
        stateNow.selectedFolder !== targetFolder
      );
    };

    try {
      if (activeSearchQuery) {
        console.log('[EmailStore] backgroundRefreshThreads - preserving active search:', activeSearchQuery);
        await get().fetchThreads({
          folder: targetFolder,
          search: activeSearchQuery,
          includeSharedMailboxThreads,
          forceRefresh: true,
        });
        return;
      }

      const response = await emailApiService.getThreads({
        folder: targetFolder,
        accountId: accountIdToUse || undefined,
        includeSharedMailboxes: includeSharedMailboxThreads,
        limit: 0,
      });
      
      const threadsData = (response as any).threads || response.data?.threads || [];
      
      if (response.success && threadsData.length > 0) {
        const newThreads: ThreadListItem[] = threadsData.map((t: any) => mapThreadSummary(t));
        
        // Check if threads actually changed by comparing IDs and timestamps
        const currentIds = new Set(currentThreads.map(t => `${t.thread_id}:${t.last_message_at}:${t.has_unread}`));
        const newIds = new Set(newThreads.map(t => `${t.thread_id}:${t.last_message_at}:${t.has_unread}`));
        
        const hasChanges = currentIds.size !== newIds.size || 
          [...currentIds].some(id => !newIds.has(id)) ||
          [...newIds].some(id => !currentIds.has(id));
        
        if (hasChanges) {
          const staleView = isStaleThreadView();
          console.log(`[EmailStore] backgroundRefreshThreads - updating with new data${staleView ? ' (cache only, view moved on)' : ''}`);
          const folderCacheKey = getThreadScopeCacheKey(targetFolder, accountIdToUse, includeSharedMailboxThreads);
          set((state) => ({
            ...(staleView ? {} : {
              threads: newThreads,
              threadsLoading: false,
            }),
            folderThreads: { ...state.folderThreads, [folderCacheKey]: newThreads },
            folderThreadsLastSync: { ...state.folderThreadsLastSync, [folderCacheKey]: Date.now() }
          }));
          
          // Update inbox unread count
          if (targetFolder === 'INBOX') {
            const unreadCount = newThreads.filter(t => t.has_unread).length;
            set({ inboxUnreadCount: unreadCount });
          }
          
          // Update IndexedDB cache (background, non-blocking)
          emailCacheService.storeThreads(newThreads, targetFolder, cacheAccountId).catch(() => {});
        } else {
          console.log('[EmailStore] backgroundRefreshThreads - no actual changes, keeping current');
          // Still update the last sync time even if no changes
          const folderCacheKey = getThreadScopeCacheKey(targetFolder, accountIdToUse, includeSharedMailboxThreads);
          set((state) => ({ 
            threadsLoading: false,
            folderThreadsLastSync: { ...state.folderThreadsLastSync, [folderCacheKey]: Date.now() }
          }));
        }
      } else if (response.success && threadsData.length === 0 && currentThreads.length > 0) {
        // Server has no threads but we have some locally - update
        const folderCacheKey = getThreadScopeCacheKey(targetFolder, accountIdToUse, includeSharedMailboxThreads);
        const staleView = isStaleThreadView();
        set((state) => ({
          ...(staleView ? {} : {
            threads: [],
            threadsLoading: false,
          }),
          folderThreads: { ...state.folderThreads, [folderCacheKey]: [] },
          folderThreadsLastSync: { ...state.folderThreadsLastSync, [folderCacheKey]: Date.now() }
        }));
        if (targetFolder === 'INBOX') {
          set({ inboxUnreadCount: 0 });
        }
      }
    } catch (error) {
      console.error('[EmailStore] backgroundRefreshThreads error:', error);
      set({ threadsLoading: false });
    }
  },

  markThreadRead: async (threadId: string, read: boolean = true) => {
    try {
      // Check if the thread was unread before marking
      const thread = get().threads.find(t => t.thread_id === threadId);
      const wasUnread = thread?.has_unread;
      const activeSharedMailboxId = get().activeSharedMailboxId;
      const includeSharedMailboxThreads = get().includeSharedMailboxThreads && !activeSharedMailboxId;
      const selectedFolder = get().selectedFolder;
      const currentUserId = get().currentUserId;
      
      console.log('[EmailStore] markThreadRead called:', {
        threadId,
        read,
        foundThread: !!thread,
        wasUnread,
        activeSharedMailboxId,
        currentUserId,
        threadHasUnread: thread?.has_unread,
        currentUnreadCounts: get().sharedMailboxUnreadCounts
      });
      
      // Optimistic update - update both threads and folderThreads cache
      set((state) => {
        const updateThread = (t: ThreadListItem) => {
          if (t.thread_id !== threadId) return t;
          
          // For shared mailboxes, update read_by_users array
          let updatedReadByUsers = t.read_by_users || [];
          if (activeSharedMailboxId && currentUserId) {
            if (read) {
              // Add current user to read_by_users if not already there
              if (!updatedReadByUsers.includes(currentUserId)) {
                updatedReadByUsers = [...updatedReadByUsers, currentUserId];
              }
            } else {
              // Remove current user from read_by_users
              updatedReadByUsers = updatedReadByUsers.filter(id => id !== currentUserId);
            }
          }
          
          return { 
            ...t, 
            has_unread: !read, 
            unread_count: read ? 0 : 1,
            read_by_users: updatedReadByUsers
          };
        };
        
        const updatedThreads = state.threads.map(updateThread);
        
        // Also update the folderThreads cache for the current folder
        // CRITICAL: Use folder:accountId format to match the cache key used everywhere else
        const folderCacheKey = getThreadScopeCacheKey(selectedFolder, activeSharedMailboxId, includeSharedMailboxThreads);
        const updatedFolderThreads = { ...state.folderThreads };
        if (updatedFolderThreads[folderCacheKey]) {
          updatedFolderThreads[folderCacheKey] = updatedFolderThreads[folderCacheKey].map(updateThread);
        }
        
        return {
          threads: updatedThreads,
          folderThreads: updatedFolderThreads,
          selectedThread: state.selectedThread && state.selectedThread.thread_id === threadId
            ? updateThread(state.selectedThread)
            : state.selectedThread,
        };
      });
      
      // Call real-time sync endpoint instead of old mark-read
      // This broadcasts the change to all devices instantly
      await emailApiService.markThreadAsReadSync(threadId, read, activeSharedMailboxId ?? undefined);
      
      // Update IndexedDB cache (background, non-blocking)
      const updatedThreads = get().threads;
      emailCacheService.storeThreads(updatedThreads, selectedFolder, getThreadScopeCacheAccountId(activeSharedMailboxId, includeSharedMailboxThreads))
        .catch(e => console.log('[EmailStore] IndexedDB cache update error (non-fatal):', e));
      
      // Refresh unread counts
      const threads = get().threads;
      
      if (activeSharedMailboxId) {
        // Viewing shared mailbox - update shared mailbox unread count
        if (read && wasUnread) {
          console.log('[EmailStore] Decrementing shared mailbox unread count for mailbox:', activeSharedMailboxId);
          get().decrementSharedMailboxUnreadCount(activeSharedMailboxId);
        } else if (!read && !wasUnread) {
          // Incrementing unread count would need a new function
          console.log('[EmailStore] Marking as unread - refresh counts');
        }
      } else if (get().selectedFolder === 'INBOX') {
        // Viewing regular inbox - update inbox unread count
        const unreadCount = threads.filter(t => t.has_unread).length;
        set({ inboxUnreadCount: unreadCount });
      }
    } catch (error) {
      console.error('[EmailStore] Error marking thread read/unread:', error);
    }
  },
  
  toggleThreadStar: async (threadId: string, starred: boolean) => {
    const selectedFolder = get().selectedFolder;
    const activeSharedMailboxId = get().activeSharedMailboxId;
    const includeSharedMailboxThreads = get().includeSharedMailboxThreads && !activeSharedMailboxId;
    const folderCacheKey = getThreadScopeCacheKey(selectedFolder, activeSharedMailboxId, includeSharedMailboxThreads);
    
    try {
      // Optimistic update - update both threads and folderThreads cache
      set((state) => {
        const updatedFolderThreads = { ...state.folderThreads };
        if (updatedFolderThreads[folderCacheKey]) {
          updatedFolderThreads[folderCacheKey] = updatedFolderThreads[folderCacheKey].map((t) =>
            t.thread_id === threadId ? { ...t, starred } : t
          );
        }
        return {
          threads: state.threads.map((t) =>
            t.thread_id === threadId ? { ...t, starred } : t
          ),
          selectedThread: state.selectedThread?.thread_id === threadId
            ? { ...state.selectedThread, starred }
            : state.selectedThread,
          folderThreads: updatedFolderThreads,
        };
      });
      
      await emailApiService.toggleThreadStarred(threadId, starred);
    } catch (error) {
      console.error('[EmailStore] Error toggling thread star:', error);
      // Revert on error
      set((state) => {
        const updatedFolderThreads = { ...state.folderThreads };
        if (updatedFolderThreads[folderCacheKey]) {
          updatedFolderThreads[folderCacheKey] = updatedFolderThreads[folderCacheKey].map((t) =>
            t.thread_id === threadId ? { ...t, starred: !starred } : t
          );
        }
        return {
          threads: state.threads.map((t) =>
            t.thread_id === threadId ? { ...t, starred: !starred } : t
          ),
          folderThreads: updatedFolderThreads,
        };
      });
    }
  },
  
  deleteThread: async (threadId: string) => {
    const thread = get().threads.find((t) => t.thread_id === threadId);
    if (!thread) return;
    
    const selectedFolder = get().selectedFolder;
    const activeSharedMailboxId = get().activeSharedMailboxId;
    const includeSharedMailboxThreads = get().includeSharedMailboxThreads && !activeSharedMailboxId;
    const accountId = getThreadScopeCacheAccountId(activeSharedMailboxId, includeSharedMailboxThreads);
    const folderCacheKey = getThreadScopeCacheKey(selectedFolder, activeSharedMailboxId, includeSharedMailboxThreads);
    
    console.log('[EmailStore] deleteThread called:', { threadId, currentFolder: thread.folder, folderCacheKey });
    
    try {
      // Check if in Trash - if so, permanently delete
      const isInTrash = thread.folder?.toLowerCase() === 'trash';
      
      // Save snapshot of current state for rollback on failure
      const previousThreads = get().threads;
      const previousFolderThreads = { ...get().folderThreads };
      const previousSelectedThread = get().selectedThread;
      
      // Optimistically remove from list AND memory cache
      // Also invalidate Trash folder cache so it re-fetches when navigated to
      const trashCacheKey = getThreadScopeCacheKey('Trash', activeSharedMailboxId, includeSharedMailboxThreads);
      set((state) => {
        const updatedFolderThreads = { ...state.folderThreads };
        if (updatedFolderThreads[folderCacheKey]) {
          updatedFolderThreads[folderCacheKey] = updatedFolderThreads[folderCacheKey].filter((t) => t.thread_id !== threadId);
        }
        // Invalidate destination folder cache so it re-fetches from server
        const updatedFolderThreadsLastSync = { ...state.folderThreadsLastSync };
        if (!isInTrash) {
          // Moving to Trash - invalidate Trash cache
          delete updatedFolderThreadsLastSync[trashCacheKey];
          delete updatedFolderThreads[trashCacheKey];
        }
        return {
          threads: state.threads.filter((t) => t.thread_id !== threadId),
          selectedThread: state.selectedThread?.thread_id === threadId ? null : state.selectedThread,
          folderThreads: updatedFolderThreads,
          folderThreadsLastSync: updatedFolderThreadsLastSync,
        };
      });
      
      const result = await emailApiService.deleteThread(threadId, isInTrash);
      console.log('[EmailStore] deleteThread API result:', result);
      
      // Check if API call actually succeeded
      if (!result.success) {
        console.error('[EmailStore] deleteThread API failed:', result.error);
        // Roll back optimistic update — restore previous state
        set({
          threads: previousThreads,
          folderThreads: previousFolderThreads,
          selectedThread: previousSelectedThread,
        });
        notify.error(result.error || 'Failed to delete email. Please try again.');
        return;
      }
      
      // Update IndexedDB cache only on success
      // IMPORTANT: Use await (not fire-and-forget) so cache is consistent
      // before any subsequent hydrateFromIndexedDB call
      if (isInTrash) {
        // Permanently deleting from Trash - remove from IndexedDB entirely
        try {
          await emailCacheService.deleteThread(threadId, accountId);
        } catch (err) {
          console.warn('[EmailStore] Failed to delete thread from cache:', err);
        }
      } else {
        // Moving to Trash - move in IndexedDB cache too
        try {
          await emailCacheService.moveThreadToFolder(threadId, 'Trash', accountId);
        } catch (err) {
          console.warn('[EmailStore] Failed to move thread to Trash in cache:', err);
        }
      }
    } catch (error) {
      console.error('[EmailStore] Error deleting thread:', error);
      // Refetch on error to restore state
      get().fetchThreads();
      notify.error('Failed to delete email. Please try again.');
    }
  },
  
  moveThread: async (threadId: string, toFolder: string) => {
    const activeSharedMailboxId = get().activeSharedMailboxId;
    const includeSharedMailboxThreads = get().includeSharedMailboxThreads && !activeSharedMailboxId;
    const accountId = getThreadScopeCacheAccountId(activeSharedMailboxId, includeSharedMailboxThreads);
    const selectedFolder = get().selectedFolder;
    const folderCacheKey = getThreadScopeCacheKey(selectedFolder, activeSharedMailboxId, includeSharedMailboxThreads);
    
    try {
      // Save snapshot for rollback
      const previousThreads = get().threads;
      const previousFolderThreads = { ...get().folderThreads };
      const previousSelectedThread = get().selectedThread;
      
      // Optimistically remove from current folder view AND memory cache
      // Also invalidate destination folder cache so it re-fetches when navigated to
      const destCacheKey = getThreadScopeCacheKey(toFolder, activeSharedMailboxId, includeSharedMailboxThreads);
      set((state) => {
        const updatedFolderThreads = { ...state.folderThreads };
        if (updatedFolderThreads[folderCacheKey]) {
          updatedFolderThreads[folderCacheKey] = updatedFolderThreads[folderCacheKey].filter((t) => t.thread_id !== threadId);
        }
        // Invalidate destination folder cache so it re-fetches from server
        const updatedFolderThreadsLastSync = { ...state.folderThreadsLastSync };
        delete updatedFolderThreadsLastSync[destCacheKey];
        delete updatedFolderThreads[destCacheKey];
        return {
          threads: state.threads.filter((t) => t.thread_id !== threadId),
          selectedThread: state.selectedThread?.thread_id === threadId ? null : state.selectedThread,
          folderThreads: updatedFolderThreads,
          folderThreadsLastSync: updatedFolderThreadsLastSync,
        };
      });
      
      const result = await emailApiService.moveThread(threadId, toFolder);
      
      // Check if API call actually succeeded
      if (!result.success) {
        console.error('[EmailStore] moveThread API failed:', result.error);
        set({
          threads: previousThreads,
          folderThreads: previousFolderThreads,
          selectedThread: previousSelectedThread,
        });
        notify.error(result.error || 'Failed to move email. Please try again.');
        return false;
      }
      
      // Update IndexedDB cache only on success
      emailCacheService.moveThreadToFolder(threadId, toFolder, accountId).catch(err => 
        console.warn('[EmailStore] Failed to move thread in cache:', err)
      );
      console.log('[EmailStore] Thread moved successfully:', { threadId, toFolder });
      return true;
    } catch (error) {
      console.error('[EmailStore] Error moving thread:', error);
      // Refetch on error to restore state
      get().fetchThreads();
      notify.error('Failed to move email. Please try again.');
      return false;
    }
  },
  
  deleteThreads: async (threadIds: string[]) => {
    const uniqueThreadIds = Array.from(new Set(threadIds.filter(Boolean)));
    if (uniqueThreadIds.length === 0) return;

    const state = get();
    const threadsById = new Map(state.threads.map((thread) => [thread.thread_id, thread]));
    const targets = uniqueThreadIds
      .map((threadId) => ({ threadId, thread: threadsById.get(threadId) }))
      .filter((target): target is { threadId: string; thread: ThreadListItem } => Boolean(target.thread));

    if (targets.length === 0) return;

    const selectedFolder = state.selectedFolder;
    const includeSharedMailboxThreads = state.includeSharedMailboxThreads && !state.activeSharedMailboxId;
    const accountId = getThreadScopeCacheAccountId(state.activeSharedMailboxId, includeSharedMailboxThreads);
    const folderCacheKey = getThreadScopeCacheKey(selectedFolder, state.activeSharedMailboxId, includeSharedMailboxThreads);
    const trashCacheKey = getThreadScopeCacheKey('Trash', state.activeSharedMailboxId, includeSharedMailboxThreads);
    const shouldInvalidateTrashCache = targets.some(({ thread }) => thread.folder?.toLowerCase() !== 'trash');
    const targetThreadIdSet = new Set(targets.map(({ threadId }) => threadId));

    const previousThreads = state.threads;
    const previousFolderThreads = { ...state.folderThreads };
    const previousFolderThreadsLastSync = { ...state.folderThreadsLastSync };
    const previousSelectedThread = state.selectedThread;

    set((currentState) => {
      const updatedFolderThreads = { ...currentState.folderThreads };
      if (updatedFolderThreads[folderCacheKey]) {
        updatedFolderThreads[folderCacheKey] = updatedFolderThreads[folderCacheKey].filter(
          (thread) => !targetThreadIdSet.has(thread.thread_id)
        );
      }

      const updatedFolderThreadsLastSync = { ...currentState.folderThreadsLastSync };
      if (shouldInvalidateTrashCache) {
        delete updatedFolderThreads[trashCacheKey];
        delete updatedFolderThreadsLastSync[trashCacheKey];
      }

      return {
        threads: currentState.threads.filter((thread) => !targetThreadIdSet.has(thread.thread_id)),
        selectedThread: currentState.selectedThread && targetThreadIdSet.has(currentState.selectedThread.thread_id)
          ? null
          : currentState.selectedThread,
        folderThreads: updatedFolderThreads,
        folderThreadsLastSync: updatedFolderThreadsLastSync,
      };
    });

    try {
      const results = await Promise.allSettled(
        targets.map(({ threadId, thread }) => emailApiService.deleteThread(threadId, thread.folder?.toLowerCase() === 'trash'))
      );

      const failures = results.filter((result) => result.status === 'rejected' || !result.value.success);

      if (failures.length > 0) {
        set({
          threads: previousThreads,
          folderThreads: previousFolderThreads,
          folderThreadsLastSync: previousFolderThreadsLastSync,
          selectedThread: previousSelectedThread,
        });

        const firstFailure = failures[0];
        const failureMessage = firstFailure.status === 'fulfilled'
          ? firstFailure.value.error
          : firstFailure.reason?.message;

        notify.error(
          failureMessage ||
          (failures.length === 1 ? 'Failed to delete email. Please try again.' : `Failed to delete ${failures.length} emails. Please try again.`)
        );
        return;
      }

      await Promise.allSettled(
        targets.map(({ threadId, thread }) => (
          thread.folder?.toLowerCase() === 'trash'
            ? emailCacheService.deleteThread(threadId, accountId)
            : emailCacheService.moveThreadToFolder(threadId, 'Trash', accountId)
        ))
      );
    } catch (error) {
      console.error('[EmailStore] Error deleting threads:', error);
      set({
        threads: previousThreads,
        folderThreads: previousFolderThreads,
        folderThreadsLastSync: previousFolderThreadsLastSync,
        selectedThread: previousSelectedThread,
      });
      notify.error('Failed to delete emails. Please try again.');
    }
  },
  
  moveThreads: async (threadIds: string[], toFolder: string) => {
    const uniqueThreadIds = Array.from(new Set(threadIds.filter(Boolean)));
    if (uniqueThreadIds.length === 0) return;

    const state = get();
    const threadsById = new Map(state.threads.map((thread) => [thread.thread_id, thread]));
    const targets = uniqueThreadIds
      .map((threadId) => ({ threadId, thread: threadsById.get(threadId) }))
      .filter((target): target is { threadId: string; thread: ThreadListItem } => Boolean(target.thread));

    if (targets.length === 0) return;

    const includeSharedMailboxThreads = state.includeSharedMailboxThreads && !state.activeSharedMailboxId;
    const accountId = getThreadScopeCacheAccountId(state.activeSharedMailboxId, includeSharedMailboxThreads);
    const selectedFolder = state.selectedFolder;
    const folderCacheKey = getThreadScopeCacheKey(selectedFolder, state.activeSharedMailboxId, includeSharedMailboxThreads);
    const destCacheKey = getThreadScopeCacheKey(toFolder, state.activeSharedMailboxId, includeSharedMailboxThreads);
    const targetThreadIdSet = new Set(targets.map(({ threadId }) => threadId));

    const previousThreads = state.threads;
    const previousFolderThreads = { ...state.folderThreads };
    const previousFolderThreadsLastSync = { ...state.folderThreadsLastSync };
    const previousSelectedThread = state.selectedThread;

    set((currentState) => {
      const updatedFolderThreads = { ...currentState.folderThreads };
      if (updatedFolderThreads[folderCacheKey]) {
        updatedFolderThreads[folderCacheKey] = updatedFolderThreads[folderCacheKey].filter(
          (thread) => !targetThreadIdSet.has(thread.thread_id)
        );
      }

      delete updatedFolderThreads[destCacheKey];

      const updatedFolderThreadsLastSync = { ...currentState.folderThreadsLastSync };
      delete updatedFolderThreadsLastSync[destCacheKey];

      return {
        threads: currentState.threads.filter((thread) => !targetThreadIdSet.has(thread.thread_id)),
        selectedThread: currentState.selectedThread && targetThreadIdSet.has(currentState.selectedThread.thread_id)
          ? null
          : currentState.selectedThread,
        folderThreads: updatedFolderThreads,
        folderThreadsLastSync: updatedFolderThreadsLastSync,
      };
    });

    try {
      const results = await Promise.allSettled(
        targets.map(({ threadId }) => emailApiService.moveThread(threadId, toFolder))
      );

      const failures = results.filter((result) => result.status === 'rejected' || !result.value.success);

      if (failures.length > 0) {
        set({
          threads: previousThreads,
          folderThreads: previousFolderThreads,
          folderThreadsLastSync: previousFolderThreadsLastSync,
          selectedThread: previousSelectedThread,
        });

        const firstFailure = failures[0];
        const failureMessage = firstFailure.status === 'fulfilled'
          ? firstFailure.value.error
          : firstFailure.reason?.message;

        notify.error(
          failureMessage ||
          (failures.length === 1 ? 'Failed to move email. Please try again.' : `Failed to move ${failures.length} emails. Please try again.`)
        );
        return;
      }

      await Promise.allSettled(
        targets.map(({ threadId }) => emailCacheService.moveThreadToFolder(threadId, toFolder, accountId))
      );

      console.log('[EmailStore] Threads moved successfully:', { threadIds: uniqueThreadIds, toFolder });
    } catch (error) {
      console.error('[EmailStore] Error moving threads:', error);
      set({
        threads: previousThreads,
        folderThreads: previousFolderThreads,
        folderThreadsLastSync: previousFolderThreadsLastSync,
        selectedThread: previousSelectedThread,
      });
      notify.error('Failed to move emails. Please try again.');
    }
  },
  
  fetchThreadStats: async () => {
    try {
      const activeSharedMailboxId = get().activeSharedMailboxId;
      const response = await emailApiService.getThreadStats({
        accountId: activeSharedMailboxId || undefined,
        includeSharedMailboxes: get().includeSharedMailboxThreads && !activeSharedMailboxId,
      });
      
      if (response.success && response.data?.stats) {
        const stats = response.data.stats;
        const folderStatsMap = stats.folders ?? {};
        set({ threadStats: stats });
        
        // Update folder-specific unread counts from stats.folders
        // This ensures badges always show even when viewing other folders
        
        // Inbox unread count (from INBOX folder stats or total unread_threads as fallback)
        const inboxUnread = getFolderStatsValue(folderStatsMap.INBOX, 'unread') || stats.unread_threads || 0;
        set({ inboxUnreadCount: inboxUnread });
        
        // Spam unread count for badge display (check both 'Spam' and 'Junk' folder names)
        const spamUnread = getFolderStatsValue(folderStatsMap.Spam, 'unread')
          || getFolderStatsValue(folderStatsMap.Junk, 'unread')
          || getFolderStatsValue(folderStatsMap.spam, 'unread')
          || 0;
        set({ spamUnreadCount: spamUnread });
        
        // Also update folder objects with unread counts for FolderList display
        const currentFolders = get().folders;
        if (currentFolders.length > 0) {
          const updatedFolders = currentFolders.map(folder => {
            const folderKey = folder.path || folder.id || folder.name;
            const folderStats = folderStatsMap[folderKey];
            if (folderStats) {
              return { ...folder, unreadCount: getFolderStatsValue(folderStats, 'unread') };
            }
            return folder;
          });
          set({ folders: updatedFolders });
        }
      }
    } catch (error) {
      console.error('[EmailStore] Error fetching thread stats:', error);
    }
  },
  
  reset: () => {
    // Disconnect WebSocket on reset
    emailSyncService.disconnect();
    set(initialState);
  },
}));
